/* CaseSubmissionPage — new aligner case wizard, as a dedicated full-page route.
 *
 * Replaces the previous Dialog-based CaseSubmissionWizard so that:
 *  - Click-outside no longer wipes the doctor's work
 *  - The flow is deep-linkable (URL carries ?draft= once Step 3 persists)
 *  - The page can be opened in a new tab / refreshed without losing state
 *  - Records uploaders are no longer cramped inside a 92vh modal
 *
 * Route: /doctor/cases/new
 * URL params:
 *   ?patient_id=<id>  pre-locks the patient (when launched from Patient Profile)
 *   ?draft=<caseId>   resumes a previously persisted draft (auto-set on Step 3)
 *
 * Same 5 steps, same fields, same backend endpoints — only the chrome changes.
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate, useSearchParams, Link, useOutletContext } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PlanTimelineSection, { RetentionRecallPair } from "@/components/case/PlanTimelineSection";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
    CaretRight, CaretLeft, CaretDown, CheckCircle, Circle, Upload, X, Check, MagnifyingGlass,
    ArrowsCounterClockwise, Crown, FileArrowUp, GitBranch, Warning, Lightning, Clock,
    Info, Tooth, Star, ShieldCheck, Cube, Cloud, CloudArrowUp, File as FileIcon, FilePdf, FileZip,
    FileVideo, Sparkle, Tag,
    ClipboardText, HeartStraight, Ruler, TrendUp,
    Sliders, Package, Prohibit, Notepad, ListBullets, Pencil, ArrowSquareUp, ArrowSquareDown,
    CircleHalf, User as UserIcon,
    Printer, BookmarkSimple, FloppyDisk, Trash, ChartBar, Hourglass, Calculator,
    Lock, UserGear, Camera, Stethoscope, HouseLine, Plus, Prescription,
    Headset, Truck, Receipt, SealCheck, Buildings, Hash, Scan, ArrowRight,
} from "@phosphor-icons/react";
import { API } from "@/lib/api";
import PerToothPlanAccordion from "@/components/case/PerToothPlanAccordion";
import DetailedClinicalExam, { ExamAccordion, present } from "@/components/case/DetailedClinicalExam";
import TreatmentObjectives from "@/components/case/TreatmentObjectives";
import CaseSubmittedDialog from "@/components/case/CaseSubmittedDialog";
import LeaveWizardDialog from "@/components/case/LeaveWizardDialog";
import ResumeDraftDialog from "@/components/case/ResumeDraftDialog";
import { computeResumeStep } from "@/components/case/DraftCasesCard";
import ScansPhotosStep from "@/components/case/ScansPhotosStep";
// iter98.99bp — FeasibilityChecks engine + UI no longer imported. The
// gate was dropped from Submit (visible ack ladder was removed in
// iter98.99w, leaving a silent block). The rule definitions are still
// present in /app/frontend/src/components/case/FeasibilityChecks.jsx
// and can be re-imported if we ever want to bring back a soft-warnings
// view — but they no longer affect the Submit button.
import AdvancePaymentBlock from "@/components/case/AdvancePaymentBlock";
import RequestScanVisitModal from "@/components/patient/RequestScanVisitModal";
import PatientAvatar from "@/components/patient/PatientAvatar";

import AddPatientDialog from "@/components/doctor/AddPatientDialog";
// iter101.91 — Extracted low-level Lab Section primitives out of this file
// (was 9,131 lines, now steadily coming down). These atoms are only used
// by Step 4 (Treatment Plan) + Step 5 (Lab Instructions).
import {
    LabSection, LabChip, LabChoiceCard, LabInfoNote, LabGlanceRow,
    FdiChipPicker, PhaseSplitVisualizer,
} from "@/components/case/wizard/lab-section";
// iter101.91 — Step 5 (Lab Instructions) extracted to its own file.
// The parent wizard just passes the shared state (rx, planTimeline,
// clinical + setters) as props. Both flows (Self-Plan doctor-authored
// and CoE planner-managed) share the same accordion stack; the wrapper
// hides planner-only sub-blocks in CoE mode.
import {
    SelfPlanLabInstructions, StepLabInstructions, TierInclusionRow,
} from "@/components/case/wizard/steps/StepLabInstructions";
// iter101.91 — Shared visual primitives (chips, section shells, step
// indicators, review helpers, draft affordances) live in one focused
// file so the wizard's visual grammar is easy to audit + update.
import {
    DraftSavedChip, ChildCaseBanner, StepIndicator, WizardStepHero,
    Step1SectionHeader, Step1ProgressSpine, Step1AnsweredStack,
    Step1AnsweredStrip, Step1ActiveWrap, Step1RecapBanner, RecapChip,
    ReviewBlock, Row, SelectField, SelectedPill,
    cardBadgeClass, resolveHeroIcon, fmtINR,
} from "@/components/case/wizard/shared";
// iter101.91 — Big chunky-card pickers (planning mode / compliance /
// scan source) live in their own file so Step 1 (Case Setup) reads as
// a clean assembly of picker components.
import {
    ScanSourcePicker, RadioRailRow, CoeSupportCompliancePicker,
    PlanningModePicker, ModeCard,
} from "@/components/case/wizard/pickers";
// iter101.91 — Preview cards + pricing pickers + uploaders + callouts.
// Consumed by Step 1 (tier/priority pickers), Step 4 (planning-software
// export card), Step 5 (Lab Instructions callouts), and Step 6 (Review
// screen — reads the prescription preview + order preview + pricing
// breakdown).
import {
    IOScannerToggleCard, TierPicker, TierFeature, LabVisitScanFeeRow,
    PromoBlock, PricingAndOffersCard, PrescriptionPreviewCard,
    PreviewBlock, PreviewRow, CoeOrderPreviewCard, PriorityPicker,
    SlotUploader, PlanningSoftwareExportCard, CoeHandoffCallout,
} from "@/components/case/wizard/blocks";
// iter101.91 — Step bodies extracted into focused per-step files.
// Was ~2,400 lines of step code inline; now the wizard shell reads as
// a clean composition of imported step components.
import { StepCaseSetup } from "@/components/case/wizard/steps/StepCaseSetup";
import { StepClinical } from "@/components/case/wizard/steps/StepClinical";
import { StepTreatmentPlan } from "@/components/case/wizard/steps/StepTreatmentPlan";
import { StepReview } from "@/components/case/wizard/steps/StepReview";
// iter101.43 — DemoFillButton re-enabled on the three clinical steps
// (Clinical Exam, Scans & Photos, Treatment Plan + Lab Instructions)
// so sales reps can rapidly fill live demo cases without touching an
// artificial "sandbox" doctor account. The button is universal — any
// authenticated doctor/staff sees it — and simply merges the returned
// demo payload into local wizard state.
import DemoFillButton from "@/components/case-wizard/DemoFillButton";
import WizardCaseHeader from "@/components/case/WizardCaseHeader";
import PrescriptionComplianceBlock from "@/components/case/PrescriptionComplianceBlock";
import WizardPaymentCommitment from "@/components/case/WizardPaymentCommitment";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { confirmDialog, alertDialog, promptDialog } from "@/lib/confirm";
import InvisiqueLogo from "@/components/site/Logo";


// iter88 — restructured wizard from 5 → 7 steps. Each step now owns
// one well-bounded concept instead of cramming three (treatment plan +
// attachments + lab instructions) into a single "Prescription" step.
/* iter97.50 — Wizard step list is dynamic by planning mode:
   - CoE (Invisique plans): 7 steps, tier picked in Step 1 (planner needs
     the budget envelope to design within).
   - Self-plan (doctor authors): 8 steps. Tier picker moves to step 7
     (after Lab Instructions) because by then the doctor knows the
     authored aligner count and can pick the cheapest fitting tier with
     full information. Review becomes step 8.
   iter97.60 — Attachments step removed (per user direction). Step 4
   "Treatment Plan" now contains Treatment Objectives + the per-tooth
   tabbed accordion (Tooth-by-tooth Movements · Attachment Placement ·
   Precision Cuts · Bite Ramps), mirroring the planner-side UI. Result:
   CoE = 6 steps, Self-plan = 7 steps.
*/
// iter97.88 — Unified 7-step wizard for BOTH planning modes. Tier +
// manufacturing speed + payment commit moved from Step 1 inline cards
// (old CoE flow) to a dedicated Step 6 "Choose Tier" screen — same
// shape as the existing self-plan flow. Symmetry win:
//   • Single mental model regardless of mode.
//   • Step 1 stays focused on patient + scan source + planning mode.
//   • Commercials get committed AFTER the clinical work is mostly
//     done — doctor has full context (case complexity, exam findings,
//     plan size) before choosing a tier.
//   • Scan-visit resume flow drops its special "Commit your commercials"
//     gate: the wizard naturally hits the tier screen at Step 6.
const STEPS_COE = [
    { id: 1, label: "Case Setup" },
    { id: 2, label: "Clinical Exam" },
    { id: 3, label: "Scans & Photos" },
    { id: 4, label: "Treatment Plan" },
    { id: 5, label: "Lab Instructions" },
    // iter98.99 — CoE Preferences step removed. Manufacturing speed +
    // planner-handoff message moved into Step 1 (Case Setup) so the
    // doctor sets all case-level expectations on one screen. Review
    // shifts up from step 7 to step 6 for CoE only.
    { id: 6, label: "Review & Submit" },
];
const STEPS_SELF = [
    { id: 1, label: "Case Setup" },
    { id: 2, label: "Clinical Exam" },
    { id: 3, label: "Scans & Photos" },
    { id: 4, label: "Treatment Plan" },
    { id: 5, label: "Lab Instructions" },
    // iter98.99o — "Choose Tier" step removed for Self-Plan. Tier is
    // now auto-assigned based on the total aligner count (upper + lower)
    // the doctor enters in Lab Instructions. Surfaced on the Review
    // screen with full transparency (price, refinement allowance,
    // retainer count). No manual selection. Self-Plan now matches
    // CoE's 6-step flow.
    { id: 6, label: "Review & Submit" },
];
// Default exported as `STEPS` (used by indicator + last-step helpers).
// Picks the CoE list since most doctors use Planning Service; the wizard
// dynamically renders the step-6 body for either mode regardless. The
// real source of truth is `stepsFor(planningMode)`.
const STEPS = STEPS_COE;
const stepsFor = (planningMode) => (planningMode === "doctor" ? STEPS_SELF : STEPS_COE);
const lastStepFor = () => STEPS.length;
const LAST_STEP = STEPS.length;

const EMPTY_CLINICAL = {
    case_type: "", malocclusion_class: "", arch: "both",
    extraction_plan: "none", extraction_teeth: "", perio_status: "",
    chief_complaint: "", special_notes: "",
    // iter84 Wave A — anamnesis depth + chairside bite measurements + scan provenance.
    medical_history: "", missing_teeth: "",
    previous_ortho: "", previous_ortho_notes: "",
    overbite_mm: "", overjet_mm: "",
    scan_device: "", scan_date: "", scan_quality_confirmed: false,
    // iter87 Wave D — scan extras.
    scan_model: "", cbct_available: false,
    // iter97.77 Wave 2 — confirmation strip (Step 3 chairside QA ticks).
    // Persist so the audit trail shows the doctor signed off on consent
    // + correct-patient before submission.
    consent_confirmed: false, case_correct_confirmed: false,
    // iter97.77 Wave 2 — sticky planning-software name (set when the
    // doctor picks a software pill in the export sub-card). Falls back
    // to "" so summary shows "—" when no export was uploaded.
    planning_software: "",
    // iter87 Wave D — §2.3 occlusal classification.
    angle_class_right: "", angle_class_left: "", angle_class_anterior: "",
    skeletal_class: "", vertical_dimension: "", midline_relationship: "",
    midline_shift_mm: "",
    // iter87 Wave D — §2.4 crowding & spacing.
    upper_arch_crowding_mm: "", lower_arch_crowding_mm: "",
    crowding_distribution: "", spacing_location: "",
    // iter87 Wave D — §2.6 posterior occlusion & crossbites.
    buccal_occlusion_right: "", buccal_occlusion_left: "",
    canine_class_right: "", canine_class_left: "",
    posterior_crossbite: false, posterior_crossbite_location: "",
    anterior_crossbite: false,
    // iter87 Wave D — §2.7 functional movements.
    anterior_guidance: "",
    lateral_movement_right: "", lateral_movement_left: "",
    protrusive_movement: "", functional_shift: false,
    // iter87 Wave D — §2.11 structured periodontal status.
    periodontal_health: "", gingival_recession: false,
    plaque_index: "", perio_compliance: "",
};
const EMPTY_RX = {
    objectives: [], ipr_preference: "", attachments_preference: "",
    elastics_preference: "", do_not_touch_teeth: "", esthetic_goals: "",
    // iter84 Wave A — lab instructions block.
    material_preference: "", lab_notes: "",
    // iter97.75 — Self-plan Lab Instructions redesign fields.
    ipr_directive: "", attachments_directive: "", elastics_directive: "",
    patient_flags: [], case_complexity: "",
    // iter98.99x — Material defaults surfaced at the top of "Aligner
    // Material Considerations". Both are auto-populated with Invisique's
    // standard configuration; doctor sees them as a read-only summary
    // of what the lab will fabricate against.
    aligner_material: "standard",
    retainer_material: "invisique_infinite",
    // iter98.99ak — Optional custom thickness hint (Self-Plan only).
    // Lives inside the Aligner Material Considerations section of Step
    // 5. Empty string = use lab's standard for the chosen sheet.
    custom_thickness_mm: "",
    shipment_preference: "", shipment_notes: "",
    // iter85 Wave B — per-tooth auxiliaries + verification.
    attachments_plan: [],
    precision_cuts: [],
    bite_ramps: { enabled: false, position: "", teeth: [], notes: "" },
    verification: {
        scans_present: false, photos_complete: false, plan_filled: false,
        lab_instructions_reviewed: false, final_review_confirmed: false,
        confirmed_at: null,
    },
    // iter89 Wave E — acknowledgments for the feasibility-check rules.
    // Keyed by rule.id (e.g. "deep_bite_warn") → bool. Persisted on the
    // case so audit records what the doctor acked at submit time.
    feasibility_acks: {},
    // iter87 Wave D — tooth-by-tooth movement matrix (self-plan only;
    // CoE wizard hides the picker, planner authors movements elsewhere).
    tooth_movements_plan: [],
};
/* iter97.39 — Phase B treatment-timeline fields live on `case.plan`
   (NOT `case.prescription`), so they're tracked in a separate state
   bucket and synced via PUT /api/cases/{id}/doctor-plan in syncDraft.
   `aligner_count_upper/lower` + `wear_schedule_default_days` were
   historically planner-authored; the wizard now asks the self-plan
   doctor for them too so the ETA chip + production specs are real. */
const EMPTY_PLAN_TIMELINE = {
    aligner_count_upper: "",
    aligner_count_lower: "",
    wear_schedule_default_days: "",
    start_date: null,
    milestones: [],
    refinement_expected: false,
    refinement_expected_reason: "",
    retention_recall_first_months: "",
    retention_recall_second_months: "",
    phase_breaks: [],
    // iter98.99p — 3rd-Party Deliverable section (Self-Plan only).
    // The doctor declares which professional planning tool authored
    // the plan + provides the live viewer URL so the lab and Invisique
    // QA can replay the setup. Walkthrough video is optional. Mirrors
    // the same fields on `CasePlan`.
    external_link: "",
    video_file_id: null,
    planning_software: "",
    planning_software_version: "",
    planning_software_other: "",
};
const EMPTY_PRICING = {
    // Priority / STAT speed-up. priority_code = "" means doctor hasn't
    // picked yet — Step 1 canNext gate enforces explicit selection.
    priority_tier_id: null, priority_code: "",
    priority_label: "", priority_fee: 0, priority_tat_days: 0,
    // Refinement / retainer auto-bypass flags. Set by the server when
    // the child case is covered by the parent's tier allowance.
    included_in_parent: false, parent_tier_id: null,
    // iter94 — Advance payment commitment (Doctor → Invisique).
    advance_amount: 0, advance_pct: 0, advance_intent_id: null,
};

// iter101.91 — `fmtINR` now imported from `@/components/case/wizard/shared`.


export default function CaseSubmissionPage() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const initialPatientId = searchParams.get("patient_id") || "";
    const draftCaseIdParam = searchParams.get("draft") || "";
    // iter90 — optional ?step=N param lets the Drafts card deep-link
    // doctors back to the step they left off on. Falls back to step 1
    // when missing so existing /doctor/cases/new (no params) behaves
    // exactly as before.
    const initialStep = (() => {
        const raw = parseInt(searchParams.get("step") || "1", 10);
        if (!Number.isFinite(raw)) return 1;
        // Use the max possible step count (self-plan=8) so we don't clamp
        // a saved deep-link prematurely; the canNext gate prevents
        // skipping required fields.
        return Math.min(Math.max(raw, 1), STEPS_SELF.length);
    })();

    const [step, setStep] = useState(initialStep);
    /* iter101.86 — Portal sidebar collapsed state, provided by
       PortalLayout via Outlet context. When the doctor collapses
       the sidebar to the icon rail we widen the wizard content
       wrapper from `max-w-5xl` (1024px) → `max-w-6xl` (1152px) so
       long fields on Steps 2–5 get more horizontal room. Falls back
       to `false` when the wizard is rendered outside the portal
       shell (e.g. tests). */
    const outletCtx = useOutletContext() || {};
    const sidebarCollapsed = !!outletCtx.sidebarCollapsed;
    const wizardMaxW = sidebarCollapsed ? "max-w-6xl" : "max-w-5xl";
    const [metadata, setMetadata] = useState(null);
    const [patients, setPatients] = useState([]);
    const [tiers, setTiers] = useState([]);
    const [priorityTiers, setPriorityTiers] = useState([]);
    const [promotions, setPromotions] = useState([]);
    const [locations, setLocations] = useState([]);
    const [practiceLocationId, setPracticeLocationId] = useState("");
    const [patientQuery, setPatientQuery] = useState("");
    const [patientId, setPatientId] = useState(initialPatientId);
    const [lockedPatient, setLockedPatient] = useState(null);
    const [diagnosis, setDiagnosis] = useState("");
    const [treatmentNotes, setTreatmentNotes] = useState("");
    const [clinical, setClinical] = useState(EMPTY_CLINICAL);
    const [rx, setRx] = useState(EMPTY_RX);
    const [planTimeline, setPlanTimeline] = useState(EMPTY_PLAN_TIMELINE);
    const [pricing, setPricing] = useState(EMPTY_PRICING);
    const [planningMode, setPlanningMode] = useState("");
    // iter98.99e1 — CoE-Support compliance opt-in. null = not yet picked
    // (no defaults — doctor must consciously choose Yes/No before the
    // Step 1 "Next" button enables). Boolean once picked. Field is locked
    // at submission server-side; this client mirror is for the wizard UX.
    const [coeSupportCompliance, setCoeSupportCompliance] = useState(null);
    const [draftCase, setDraftCase] = useState(null);
    // iter100.278 — Track the last successful autosave so the wizard
    // can show a subtle "Draft saved just now" / "Saved 3m ago" chip.
    // Doctors were nervous about losing work mid-flow; this reassures
    // them without being noisy.
    const [lastSavedAt, setLastSavedAt] = useState(null);
    const [uploads, setUploads] = useState({});         // slot_key -> {file, uploading, uploaded}
    const [busy, setBusy] = useState(false);
    const [hydrating, setHydrating] = useState(true);
    // iter98.69 — Premium "case submitted" confirmation dialog. Replaces
    // the thin sonner toast that used to confirm one of the doctor's
    // most consequential actions. Captured snapshot of the case at
    // submit time so the dialog can render even after navigate() fires.
    const [submittedDialog, setSubmittedDialog] = useState({ open: false, flow: "planning", code: "", caseId: "", visitMeta: null, redirect: null });
    // iter101.74 — Premium "Leave the wizard?" confirmation dialog that
    // mirrors CaseSubmittedDialog's aesthetic. Replaces the plain
    // confirmDialog() used before so the "you can come back" narrative
    // gets the same treatment as the celebratory submit flow.
    const [leaveWizard, setLeaveWizard] = useState({ open: false });
    // iter101.75 — "Welcome back" resume dialog. Fires on mount when the
    // wizard hydrates from a real draft (not a fresh stub) so the doctor
    // sees a hero of progress-so-far and knows exactly which step they're
    // dropping onto. Silent for brand-new cases.
    const [resumeDraft, setResumeDraft] = useState({ open: false, snapshot: null, step: 1 });
    // iter101.85 — Silent "draft saved" toast on any nav exit that
    // ISN'T going through the explicit Cancel dialog / Discard / Submit
    // handlers. Autosave already persists every field per step, so we
    // don't need to block sidebar clicks or browser back — we just
    // reassure the doctor with a toast on unmount. `cleanExitRef`
    // guards against firing the toast on Save-as-Draft / Discard /
    // Submit paths (which already show their own toasts / dialogs).
    // `draftRef` mirrors draftCase into a ref so the cleanup callback
    // can read the *current* draft at unmount time (the closure over
    // `draftCase` from a plain useEffect([]) would only see the initial
    // null value).
    const cleanExitRef = useRef(false);
    const draftRef = useRef(null);
    // Refinement / retainer allowance result. Null until we've checked
    // (only relevant for child cases with a parent_case_id).
    const [allowance, setAllowance] = useState(null);
    // iter92 — admin-editable planning-mode card copy. Null until fetched;
    // PlanningModePicker falls back to hardcoded defaults if missing.
    const [planningCards, setPlanningCards] = useState(null);
    // iter92 — admin-editable wizard text snippets (page headline, etc.).
    // Shape: { values: {key: text}, slots: [...] }. Falls back to defaults.
    const [wizardCopy, setWizardCopy] = useState(null);
    // iter92 — doctor's tier insights (e.g., "YOUR FAVOURITE" badge based on
    // case history). Null when not enough data points exist.
    const [tierInsights, setTierInsights] = useState(null);
    // iter97.50 — Live IO Scanner pickup fee (admin-configurable). Fetched
    // once on mount; falls back to ₹3,000 default if the endpoint is
    // unreachable. The wizard renders this as a live number on the toggle
    // card so the doctor always sees the current rate.
    const [ioScannerFee, setIoScannerFee] = useState(3000);
    useEffect(() => {
        api.get("/settings/io-scanner-fee")
            .then((r) => setIoScannerFee(Number(r.data?.fee ?? 3000)))
            .catch(() => setIoScannerFee(3000));
    }, []);
    // iter98.99o — Compliance Monitoring & Support fee, admin-configurable.
    // Defaults to ₹0 (offered free). Surfaced on the Step 1 Compliance
    // card so the doctor always sees the live rate before opting in.
    const [complianceFee, setComplianceFee] = useState(0);
    useEffect(() => {
        api.get("/settings/compliance-fee")
            .then((r) => setComplianceFee(Number(r.data?.fee ?? 0)))
            .catch(() => setComplianceFee(0));
    }, []);

    // iter98.99q — Live admin-editable wizard card copy:
    //   • Scan Source picker rows (self-capture + IO Scans)
    //   • Compliance Monitoring & Support card
    //   • IO Scans hero panel (after IO is picked)
    // All three default to null on initial mount; the wizard falls back
    // to hardcoded copy if a row is missing or the request fails.
    const [scanSourceCards, setScanSourceCards] = useState(null);
    const [complianceCard, setComplianceCard]   = useState(null);
    const [scanServiceHero, setScanServiceHero] = useState(null);
    useEffect(() => {
        api.get("/wizard/scan-source-cards").then((r) => setScanSourceCards(r.data)).catch(() => {});
        api.get("/wizard/compliance-card").then((r) => setComplianceCard(r.data)).catch(() => {});
        api.get("/wizard/scan-service-hero").then((r) => setScanServiceHero(r.data)).catch(() => {});
    }, []);
    // iter97.83 — Scan source picker state. Drives the new in-Step-1
    // 2-card fork that lets the doctor pick "Upload my own scans" vs
    // "Book Invisique's IO Scanning Service" RIGHT after they pick the
    // patient + submission mode. Until the doctor picks one of the two
    // we hide tier / pricing / advance below (those decisions all wait
    // on a real scan source). For loaded drafts we default to "upload"
    // because the user already committed to the upload flow when the
    // draft was created.
    const [scanSource, setScanSource] = useState("");
    // iter97.83 — Doctor's declared scanner info (from Profile iter97.80).
    // Drives the "Upload my own scans" card subtitle so it feels personal
    // ("your TRIOS 4 Wireless is ready"). Null when the doctor hasn't
    // declared in-house scanning yet.
    const [scannerProfile, setScannerProfile] = useState(null);
    // iter98.99an — Prescription compliance gate. `prescriptionSigned`
    // is set to true once the backend confirms the doctor has captured
    // a signature + ticked all 4 consent boxes. The wizard's Submit
    // button reads this in its disabled-state computation.
    const [prescriptionSigned, setPrescriptionSigned] = useState(false);
    // Reset the gate whenever we switch cases / drafts.
    useEffect(() => { setPrescriptionSigned(false); }, [draftCaseIdParam]);
    /* iter98.99ai — Imperative bridge to the Step-6 PrescriptionComplianceBlock.
       We removed the standalone "Sign prescription" CTA inside the block
       and merged signing into the wizard's bottom "Sign & Submit to Lab"
       button. The block exposes { signNow, ready, isAlreadySigned } via
       this ref so submitCase() can POST /prescription/sign first and only
       then POST /submit. Both round-trips fire from a single click. */
    const prescriptionSignApiRef = useRef(null);
    // iter97.83 — Wizard quick-create modal toggle. Opens when the doctor
    // picks the IO Scanning Service card; modal POSTs auto_create_case=true
    // and the page navigates to the new case on success.
    const [scanVisitModalOpen, setScanVisitModalOpen] = useState(false);
    // iter97.87 — Inline Add Patient dialog. Lets the doctor mint a fresh
    // patient row WITHOUT leaving the wizard. On success we refresh the
    // patient list + auto-select the new patient so the wizard advances
    // smoothly.
    const [addPatientOpen, setAddPatientOpen] = useState(false);
    // iter101.76 — Inline "Create '{query}' as a new patient" flow.
    // When the doctor picks the create suggestion from an empty
    // search-results dropdown, we open the AddPatientDialog with the
    // query prefilled into the Name field so the doctor doesn't have
    // to retype what they just searched for.
    const [addPatientPrefillName, setAddPatientPrefillName] = useState("");
    /* iter98.99bn — Demo-fill state. Loaded on mount; lets per-step Fill
       buttons paint realistic clinical data without bundling fixtures in
       the JS bundle. `demoScenarioIdx` cycles across wizard sessions via
       localStorage so two consecutive demos don't show identical data. */
    const [demoScenarios, setDemoScenarios] = useState([]);
    const [demoScenarioIdx] = useState(() => {
        try {
            const raw = parseInt(localStorage.getItem("invisique:demoScenarioIdx") || "0", 10);
            const next = (Number.isFinite(raw) ? raw : 0);
            localStorage.setItem("invisique:demoScenarioIdx", String((next + 1) % 3));
            return next;
        } catch { return 0; }
    });
    useEffect(() => {
        api.get("/doctor/profile")
            .then((r) => setScannerProfile(r.data || null))
            .catch(() => setScannerProfile(null));
    }, []);
    /* iter98.99bn — Fetch demo scenarios once. Silently swallow errors —
       the Fill buttons just disable themselves if the catalogue isn't
       reachable (e.g. backend cold start). */
    useEffect(() => {
        api.get("/demo/scenarios")
            .then((r) => setDemoScenarios(r.data || []))
            .catch(() => setDemoScenarios([]));
    }, []);

    /* iter101.85 — Silent-save toast on nav exit.
       ─────────────────────────────────────────────
       The wizard already autosaves every field per step, so navigating
       away via the sidebar (or browser Back, or clicking a top-nav link)
       doesn't lose any data. But without ANY feedback, doctors reported
       feeling anxious about "did my work save?" — so we mirror the
       modern SaaS pattern (Notion / Linear / Figma): stay silent during
       nav, but fire a small confirmation toast the moment the wizard
       tears down.

       Firing rules:
       - Only when a draftCase exists (nothing to save otherwise).
       - Skipped when `cleanExitRef.current === true` (set by the
         explicit exit paths: Save-as-Draft, Discard, and Submit — each
         already shows its own toast / hero dialog).
    */
    useEffect(() => { draftRef.current = draftCase; }, [draftCase]);
    useEffect(() => {
        return () => {
            const d = draftRef.current;
            if (d && !cleanExitRef.current) {
                toast.success(`Draft saved · ${d.code || "IVQ-CRN-…"} · Resume any time from My Cases`);
            }
        };
    }, []);
    const currentDemoScenario = demoScenarios[demoScenarioIdx % Math.max(1, demoScenarios.length)] || null;
    // Helper to read a slot value with hardcoded fallback for safety.
    const copy = (key, fallback) => wizardCopy?.values?.[key] || fallback;
    // iter92.7 — Step labels can be admin-overridden. Defaults fall back to
    // STEPS module constant so the wizard never breaks if /wizard/copy fails.
    // iter97.50 — step list now depends on planning mode (self-plan has an
    // extra "Choose Tier" step). Recompute when planningMode toggles.
    const steps = useMemo(() => stepsFor(planningMode).map((s) => ({
        ...s,
        label: copy(`step.label.${s.id}`, s.label),
    })), [wizardCopy, planningMode]);
    const lastStep = steps.length;

    /* Load metadata + supporting lists on mount. */
    useEffect(() => {
        let cancelled = false;
        Promise.all([
            api.get("/cases/metadata").then((r) => r.data).catch(() => null),
            api.get("/doctor/patients").then((r) => r.data).catch(() => []),
            api.get("/tiers/active").then((r) => r.data).catch(() => []),
            api.get("/promotions/active").then((r) => r.data).catch(() => []),
            api.get("/doctor/practice-locations").then((r) => r.data).catch(() => []),
            api.get("/priority-tiers/active").then((r) => r.data).catch(() => []),
            api.get("/wizard/planning-mode-cards").then((r) => r.data).catch(() => null),
            api.get("/wizard/copy").then((r) => r.data).catch(() => null),
            api.get("/doctor/tier-insights").then((r) => r.data).catch(() => null),
        ]).then(([m, ps, ts, prs, locs, pts, pmc, wc, ti]) => {
            if (cancelled) return;
            setMetadata(m);
            setPatients(ps || []);
            setTiers(ts || []);
            setPromotions(prs || []);
            setLocations(locs || []);
            setPriorityTiers(pts || []);
            setPlanningCards(pmc);
            setWizardCopy(wc);
            setTierInsights(ti);
            // Pre-select primary practice location.
            const primary = (locs || []).find((l) => l.is_primary) || (locs || [])[0];
            if (primary) setPracticeLocationId(primary.id);
            // If the caller passed ?patient_id=, mark that patient as locked
            // (chosen from a Patient Profile context — doctor can't switch).
            if (initialPatientId) {
                const p = (ps || []).find((x) => x.id === initialPatientId);
                if (p) setLockedPatient(p);
            }
        }).finally(() => { if (!cancelled) setHydrating(false); });
        return () => { cancelled = true; };
    }, [initialPatientId]);

    /* iter97.90 — Stub-case auto-mint on /doctor/cases/new mount.
       Pre-issues an IVQ-CRN-XXXX code so the wizard chrome carries it
       from Step 1 — before the doctor has typed anything. Skipped when
       ?draft= is already in the URL (the resume effect handles that)
       and when we're in a child-case spawn flow.

       The stub effect runs once on mount: it fires off POST /cases/stub
       (passing initial_patient_id if the doctor came from a Patient
       Profile context), then stamps ?draft=<id> into the URL so refresh
       resumes seamlessly. Failure is non-fatal — the legacy ensureDraft
       path on Step 2 → Step 3 transition still works as a fallback. */
    const stubMintedRef = useRef(false);
    useEffect(() => {
        if (draftCaseIdParam) return;        // resuming existing
        if (stubMintedRef.current) return;   // already minted in this session
        stubMintedRef.current = true;
        (async () => {
            try {
                const body = initialPatientId ? { patient_id: initialPatientId } : {};
                const r = await api.post("/cases/stub", body);
                setDraftCase(r.data);
                if (r.data.patient_id) setPatientId(r.data.patient_id);
                if (r.data.practice_location_id) setPracticeLocationId(r.data.practice_location_id);
                if (r.data.planning_mode) setPlanningMode(r.data.planning_mode);
                // iter98.99e1 — hydrate the CoE-Support opt-in from the
                // freshly-minted stub. Stubs come back with `null`, which
                // matches our initial state.
                if (typeof r.data.coe_support_compliance === "boolean") {
                    setCoeSupportCompliance(r.data.coe_support_compliance);
                }
                // Read fresh URL params at the time of the call — closure
                // capture races with React StrictMode double-mount.
                const next = new URLSearchParams(window.location.search);
                next.set("draft", r.data.id);
                setSearchParams(next, { replace: true });
            } catch (err) {
                // Non-fatal — case code will be minted later by ensureDraft
                // on Step 2 → Step 3. Reset the sentinel so a retry next
                // navigation can attempt to mint again.
                stubMintedRef.current = false;
                // eslint-disable-next-line no-console
                console.warn("Stub case mint failed; falling back to lazy mint", err);
            }
        })();
        // No cleanup — stubMintedRef gating is the idempotency guard.
        // A `cancelled` flag here would race with StrictMode's
        // double-mount cleanup and silently drop the setDraftCase.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* Resume an in-progress draft when ?draft= is in the URL.
       Re-hydrates patient + clinical + prescription + pricing so the doctor
       can pick up where they left off after a refresh or accidental close.
       Also rehydrates the per-slot `uploads` state from already-uploaded
       case files so the Records step doesn't pretend they're missing.

       Child-case awareness: when the resumed draft is a refinement or
       retainer (has `parent_case_id`), we also lock the patient picker
       and surface a banner above the wizard so the doctor never wonders
       why they can't change the patient. */
    useEffect(() => {
        if (!draftCaseIdParam) return;
        let cancelled = false;
        api.get(`/cases/${draftCaseIdParam}`).then((r) => {
            if (cancelled) return;
            const c = r.data;
            setDraftCase(c);
            if (c.patient_id) setPatientId(c.patient_id);
            // iter97.88 — Resuming a scan_visit_completed case: the
            // patient + scan source were both committed when the doctor
            // booked the scan visit. Lock the patient picker (same
            // mechanism used for child cases) and force scan_source to
            // "upload" so Step 1 reads as "done — choose planning mode
            // and continue". The actual scan + photo files arrive via
            // the existing c.files hydration loop below — the technician's
            // uploads were stamped with this case_id when the visit
            // completed.
            const isScanVisitResume = c.status === "scan_visit_completed";
            if (isScanVisitResume && c.patient_id) {
                setLockedPatient({
                    id: c.patient_id,
                    name: c.patient_name || "Patient",
                    phone: c.patient_phone || "",
                    code: c.patient_code || "",
                });
            }
            if (c.parent_case_id && c.patient_id) {
                // Lock the patient picker — child cases inherit the parent's
                // patient and the doctor must not be able to change it.
                // Build lockedPatient directly off the child case's enriched
                // fields (patient_name / patient_phone are populated by
                // _enrich_case on the backend) instead of refetching
                // /doctor/patients — the latter races with the initial-mount
                // patients load and would leave the lock briefly empty.
                setLockedPatient({
                    id: c.patient_id,
                    name: c.patient_name || "Patient",
                    phone: c.patient_phone || "",
                    code: c.patient_code || "",
                });
                // Child case: check the parent's tier allowance so the
                // wizard can either auto-bypass the tier picker or show
                // "you'll be charged for this many overage stages" early.
                const kind = c.case_kind === "retainer" ? "retainer" : "refinement";
                api.get(`/doctor/cases/${c.parent_case_id}/child-allowance`, {
                    params: { kind, requested_stages: 0 },
                })
                    .then((res) => {
                        if (cancelled) return;
                        setAllowance(res.data);
                        // When the parent's tier covers this child case
                        // entirely, pre-fill the pricing snapshot with the
                        // parent's tier id + zeroed amounts. The doctor
                        // never sees the picker and Step 1 validates
                        // without them touching anything.
                        if (res.data?.covered) {
                            setPricing((p) => ({
                                ...p,
                                tier_id: res.data.tier_id,
                                tier_name: res.data.tier_name,
                                lab_charge: 0,
                                final_charge: 0,
                                included_in_parent: true,
                                parent_tier_id: res.data.tier_id,
                            }));
                        }
                    })
                    .catch(() => { /* non-fatal — picker remains visible */ });
            }
            if (c.diagnosis) setDiagnosis(c.diagnosis);
            if (c.treatment_notes) setTreatmentNotes(c.treatment_notes);
            if (c.clinical) setClinical({ ...EMPTY_CLINICAL, ...c.clinical });
            if (c.prescription) setRx({ ...EMPTY_RX, ...c.prescription });
            // iter97.39 — hydrate Phase B timeline fields from case.plan
            // (separate persistence bucket from prescription).
            if (c.plan) {
                setPlanTimeline((pt) => ({
                    ...EMPTY_PLAN_TIMELINE,
                    aligner_count_upper: c.plan.aligner_count_upper ?? "",
                    aligner_count_lower: c.plan.aligner_count_lower ?? "",
                    wear_schedule_default_days: c.plan.wear_schedule_default_days ?? "",
                    start_date: c.plan.start_date ?? null,
                    milestones: c.plan.milestones || [],
                    refinement_expected: !!c.plan.refinement_expected,
                    refinement_expected_reason: c.plan.refinement_expected_reason || "",
                    retention_recall_first_months: c.plan.retention_recall_first_months ?? "",
                    retention_recall_second_months: c.plan.retention_recall_second_months ?? "",
                    phase_breaks: c.plan.phase_breaks || [],
                    // iter98.99p — 3rd-Party Deliverable hydration.
                    external_link: c.plan.external_link || "",
                    video_file_id: c.plan.video_file_id || null,
                    planning_software: c.plan.planning_software || "",
                    planning_software_version: c.plan.planning_software_version || "",
                    planning_software_other: c.plan.planning_software_other || "",
                }));
            }
            if (c.pricing) setPricing({ ...EMPTY_PRICING, ...c.pricing });
            if (c.practice_location_id) setPracticeLocationId(c.practice_location_id);
            if (c.planning_mode) setPlanningMode(c.planning_mode);
            else if (c.status === "scan_visit_completed") {
                // iter97.88 — Force the doctor to explicitly pick planning
                // mode on resume; default "coe" would make the banner skip
                // the picker, which contradicts "doctor commits to planning
                // mode before continuing" UX.
                setPlanningMode("");
            }
            // iter98.99e1 — hydrate the CoE-Support opt-in on resume. A
            // boolean (true/false) means the doctor already picked; null
            // means they haven't and the Step 1 gate will force them to.
            if (typeof c.coe_support_compliance === "boolean") {
                setCoeSupportCompliance(c.coe_support_compliance);
            } else {
                setCoeSupportCompliance(null);
            }
            // iter97.83 — Resuming a draft → the doctor already committed
            // to the upload flow when the draft was created (otherwise the
            // case would be in `scan_visit_scheduled`, not `draft`).
            // iter98.99x — With the stub-mint flow, /doctor/cases/new now
            // creates an empty draft up-front so the doctor can be
            // navigated by ?draft= immediately. That meant every fresh
            // case landed here with scanSource auto-forced to "upload",
            // skipping the doctor's conscious pick. Detect a "fresh stub"
            // (no diagnosis, no clinical input, no files, no planning
            // mode picked) and leave scanSource empty so the Step 1
            // picker appears. Real resumes still skip the picker.
            //
            // iter100.157ac — Removed the `!c.patient_id` requirement.
            // When a doctor initiates a case from the My Patients tab
            // ("Create new case" on the patient detail), the stub is
            // pre-populated with `patient_id`. The old check treated
            // that as a "real resume" and force-committed scanSource,
            // causing Step 2 (Treatment Planning Mode) to render
            // alongside Step 1 — breaking the progressive disclosure
            // rule that only one step is visible at a time.
            const isFreshStub = (
                c.status === "draft"
                && !c.diagnosis
                && !c.planning_mode
                && (!c.files || c.files.length === 0)
                && Object.keys(c.clinical || {}).length === 0
                && !c.parent_case_id
            );
            if (!isFreshStub) {
                setScanSource("upload");
            }
            const hydratedUploads = {};
            (c.files || []).forEach((f) => {
                if (!f.is_deleted) hydratedUploads[f.kind] = { name: f.name, uploaded: true, id: f.id };
            });
            setUploads(hydratedUploads);
            // iter98.99an — Hydrate the prescription compliance gate on
            // draft resume. If the doctor previously signed this draft
            // (re-entering after a save), the gate is already open.
            const pc = c.prescription_compliance;
            const pcChecklistTicked = pc && pc.checklist && [
                pc.checklist.treatment_plan_approved,
                pc.checklist.patient_consent_confirmed,
                pc.checklist.prescription_reviewed,
                pc.checklist.compliance_acknowledged,
            ].every(Boolean);
            const pcHasSig = !!(pc && (pc.signature_data_url || pc.signature_typed_name));
            setPrescriptionSigned(!!(pcChecklistTicked && pcHasSig && pc.signed_at));
            // iter98.99x — Suppress "Draft resumed" toast for fresh stubs
            // (the doctor just landed on /doctor/cases/new — nothing to
            // "resume" yet). Only signal resume when there's real work
            // to come back to.
            // iter101.75 — For real resumes we now show a premium
            // ResumeDraftDialog hero instead of the tiny toast so the
            // doctor sees exactly what's been done and which step they
            // land on. `computeResumeStep` mirrors the deep-link logic
            // used by the drafts list so both places agree on the
            // resume step.
            if (!isFreshStub) {
                const resumeStepCalc = computeResumeStep(c);
                setResumeDraft({
                    open: true,
                    snapshot: c,
                    step: resumeStepCalc,
                });
            }
        }).catch(() => {
            toast.error("Could not resume the draft — starting fresh");
        });
        return () => { cancelled = true; };
    }, [draftCaseIdParam]);

    const filteredPatients = useMemo(() => {
        // Only show suggestions once the doctor starts typing — keeps the
        // picker clean and avoids dumping the entire patient list.
        if (!patientQuery) return [];
        const q = patientQuery.toLowerCase();
        return patients.filter((p) =>
            (p.name || "").toLowerCase().includes(q) ||
            (p.phone || "").includes(patientQuery)
        ).slice(0, 15);
    }, [patients, patientQuery]);

    const selectedPatient = patients.find((p) => p.id === patientId);

    /* iter97.49 — when planning_mode toggles AFTER a tier is already picked,
       the tier's effective lab_charge may shift (CoE → self-plan can be
       cheaper). Recompute pricing.lab_charge from the current tier + new
       mode so the Step 7 review and the live promo math stay accurate. */
    useEffect(() => {
        if (!pricing.tier_id) return;
        const tier = tiers.find((t) => t.id === pricing.tier_id);
        if (!tier) return;
        // iter98.99au — Effective lab charge per planning mode AND discount type.
        // Self-Plan applies the tier's self_plan_discount (flat ₹ or %); CoE uses
        // the full lab_charge.
        const labChargeRaw = Number(tier.lab_charge || 0);
        const next = planningMode === "doctor"
            ? (() => {
                const d = Number(tier.self_plan_discount || 0);
                const t = (tier.self_plan_discount_type || "flat").toLowerCase();
                return t === "percent"
                    ? Math.max(0, labChargeRaw * (1 - d / 100))
                    : Math.max(0, labChargeRaw - d);
            })()
            : labChargeRaw;
        if (Number(pricing.lab_charge || 0) === next) return; // no-op shortcut
        setPricing((p) => {
            const fee = Number(p.priority_fee || 0);
            return {
                ...p,
                lab_charge: next,
                // Tier price changed — any previously-applied promo math is
                // now stale (% off a different number). Reset to keep totals
                // honest; doctor can re-apply the code if needed.
                promo_id: null, promo_code: "", discount_type: null,
                discount_value: 0, discount_amount: 0,
                final_charge: Math.max(0, next + fee + Number(p.scanner_pickup_fee || 0)),
            };
        });
    }, [planningMode, pricing.tier_id, tiers]);  // eslint-disable-line react-hooks/exhaustive-deps

    /* Validation per step. Kept loose on Steps 2-4 so doctors (and QA) can
       move through the flow quickly during testing — only patient (Step 1)
       and tier (Step 1 for CoE / Step 7 for self-plan) are truly required
       because the backend can't persist a case without them.

       iter97.50 — self-plan defers tier selection to Step 7 ("Choose Tier"),
       so Step 1 only requires patient + mode. CoE keeps the original
       Step 1 gate (patient + tier).
    */
    const canNext = useMemo(() => {
        const isSelfPlan = planningMode === "doctor";
        if (step === 1) {
            // iter97.83 — Brand-new (non-child) cases must commit to a
            // scan source before advancing. When the doctor picks
            // "io_service" they leave the wizard via the booking modal,
            // so reaching "Next" implies they picked "upload" (or it's
            // a child case which skips the picker).
            const isChildCase = !!draftCase?.parent_case_id;
            if (!isChildCase && scanSource !== "upload") return false;
            // iter98.99x — Step 1 also gates on Submission mode +
            // Manufacturing speed. Both used to have hardcoded defaults
            // ("coe" / "standard") which let the doctor breeze past
            // without making a conscious decision. Now both are empty
            // by default and the doctor must explicitly pick.
            if (!isChildCase && !planningMode) return false;
            if (!isChildCase && !(pricing.priority_code || "").trim()) return false;
            // iter98.99e1 — The CoE-Support compliance opt-in lives in
            // Step 1, right after the Submission mode picker. Doctor MUST
            // explicitly answer Yes or No before Next enables (true "no
            // defaults" per product direction). Child cases skip this —
            // refinements/retainers inherit the parent's compliance setup.
            if (!isChildCase && typeof coeSupportCompliance !== "boolean") return false;
            // iter97.88 — Tier no longer captured on Step 1 for either
            // mode — both modes now share the dedicated Step 6 "Choose
            // Tier" screen. Step 1 just gates on patient + scan source.
            return !!patientId;
        }
        // iter98.99o — Self-Plan no longer routes through Step 6
        // "Choose Tier". The `tier_id` gate is moot; auto-assignment
        // handles it. Step 6 is Review & Submit in both modes, with
        // a server-side feasibility check on submit + a frontend
        // submit-button gate (see `canSubmit` below) for the 41+
        // aligners overflow case.
        if (step === 6) return true;
        return true;
    }, [step, patientId, pricing, planningMode, scanSource, draftCase, coeSupportCompliance]);

    /* Auto-assigned tier — iter98.99au
     *
     * Self-Plan flow: the "Choose Tier" step is gone. The doctor
     * enters Upper + Lower aligner counts in Lab Instructions
     * (Step 5), and this memo maps the total to the matching tier
     * from the admin-configured `tiers` collection.
     *
     * Range logic (admin-managed via `TreatmentTier.aligner_min`
     * + `TreatmentTier.aligner_count` (= aligner_max)):
     *   First sort active tiers ASC by aligner_count, then pick the
     *   FIRST tier where total falls within [aligner_min, aligner_count].
     *   If `aligner_min` is 0 / unset (legacy tier), only the upper
     *   cap is checked. If total < min of every tier → "below
     *   minimum" error. If total > max of every tier → "above max"
     *   error.
     *
     * Consumers — see iter98.99o comment block below. */
    const autoSelfPlanTier = useMemo(() => {
        if (planningMode !== "doctor") return null;
        const u = Number(planTimeline?.aligner_count_upper) || 0;
        const l = Number(planTimeline?.aligner_count_lower) || 0;
        /* iter98.99dc — Stage count = max(U, L) per the unified app rule
           (1 stage = 1 upper + 1 lower piece, shared by planner + admin
           + production). Previously we summed U + L which over-counted
           against tier ranges defined in stages. */
        const total = Math.max(u, l);
        if (total <= 0) {
            return { total, tier: null, error: null, isReady: false };
        }
        const activeTiers = (tiers || []).filter((t) => t.is_active !== false);
        if (activeTiers.length === 0) {
            return { total, tier: null, error: null, isReady: false };
        }
        const sortedTiers = activeTiers
            .slice()
            .sort((a, b) => (Number(a.aligner_count) || 0) - (Number(b.aligner_count) || 0));
        // Global minimum across all active tiers — used for the "below minimum" error.
        const minRequired = Math.min(...activeTiers.map((t) => Number(t.aligner_min) || 0).filter((n) => n > 0));
        const hasMin = Number.isFinite(minRequired) && minRequired > 0;
        if (hasMin && total < minRequired) {
            return {
                total,
                tier: null,
                error: `Minimum ${minRequired} stages required (you have ${total}). Increase Upper / Lower aligner counts to match the smallest tier.`,
                isReady: true,
            };
        }
        // Match the first tier where aligner_min ≤ stages ≤ aligner_count.
        // For legacy tiers (aligner_min=0/unset) the lower bound is treated
        // as 0 so the existing "up to X" semantics still work.
        const fit = sortedTiers.find((t) => {
            const mn = Number(t.aligner_min) || 0;
            const mx = Number(t.aligner_count) || 0;
            return total >= mn && total <= mx;
        });
        if (!fit) {
            const maxCap = Math.max(...sortedTiers.map((t) => Number(t.aligner_count) || 0));
            return {
                total,
                tier: null,
                error: `Your plan has ${total} stages, but the maximum is ${maxCap}. Please revise your upper and/or lower aligner counts.`,
                isReady: true,
            };
        }
        return { total, tier: fit, error: null, isReady: true };
    }, [planningMode, planTimeline?.aligner_count_upper, planTimeline?.aligner_count_lower, tiers]);

    /* Auto-tier sync — iter98.99o
     *
     * Keeps `pricing.tier_*` + `pricing.lab_charge` aligned with the
     * auto-assigned tier so the existing submission payload (which
     * reads tier_id, tier_name, lab_charge etc.) carries the
     * correct values without any UI plumbing. Runs only for
     * Self-Plan and only when we have a valid tier (no error). */
    useEffect(() => {
        if (planningMode !== "doctor") return;
        if (!autoSelfPlanTier?.tier) return;
        const t = autoSelfPlanTier.tier;
        // iter98.99au — Effective lab charge: respects discount_type.
        const labRaw = Number(t.lab_charge || 0);
        const d = Number(t.self_plan_discount || 0);
        const dt = (t.self_plan_discount_type || "flat").toLowerCase();
        const labCharge = dt === "percent"
            ? Math.max(0, labRaw * (1 - d / 100))
            : Math.max(0, labRaw - d);
        setPricing((p) => {
            // Skip if already in sync — prevents pointless re-renders.
            if (p.tier_id === t.id && Number(p.lab_charge || 0) === labCharge) return p;
            const discount = Number(p.discount_amount || 0);
            const priorityFee = Number(p.priority_fee || 0);
            const scannerFee = Number(p.scanner_pickup_fee || 0);
            const finalCharge = Math.max(0, labCharge - discount + priorityFee + scannerFee);
            return {
                ...p,
                tier_id: t.id,
                tier_name: t.name,
                aligner_count: Number(t.aligner_count) || 0,
                refinement_included: !!t.refinement_included,
                refinement_count: Number(t.refinement_count ?? 1),
                refinement_aligner_count: Number(t.refinement_aligner_count ?? 0),
                retainers_included: !!t.retainers_included,
                included_retainer_count: Number(t.included_retainer_count ?? (t.retainers_included ? 1 : 0)),
                lab_charge: labCharge,
                final_charge: finalCharge,
            };
        });
    }, [planningMode, autoSelfPlanTier?.tier, setPricing]);

    /* Auto-scroll to the top whenever the step changes so the doctor lands
       on the first field of the new step (the sticky header keeps the step
       indicator visible). Without this they'd land mid-page wherever the
       last step's scroll position was. */
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, [step]);

    /* When advancing past Step 2 (Clinical) we persist the draft to the
       backend so uploads can attach to a real case_id. We also stamp the
       new draft id into the URL (?draft=) so a hard refresh resumes work. */
    // iter84 — strip empty-string numeric fields before sending so Pydantic
    // doesn't reject `""` for Optional[float]. Server also tolerates empty
    // strings via validate_clinical, but Pydantic v2 fails earlier — clean
    // payload here keeps both sides happy.
    const cleanClinical = useCallback(() => {
        const out = { ...clinical };
        // iter87 — strip all empty-string numerics across Waves A + D.
        // The wizard stores "" for cleared number inputs; Pydantic
        // Optional[float] rejects "" but accepts absent fields.
        [
            "overbite_mm", "overjet_mm",
            "upper_arch_crowding_mm", "lower_arch_crowding_mm",
            "midline_shift_mm",
        ].forEach((k) => {
            if (out[k] === "" || out[k] == null) delete out[k];
        });
        return out;
    }, [clinical]);
    const ensureDraft = useCallback(async () => {
        if (draftCase) return draftCase;
        setBusy(true);
        try {
            const r = await api.post("/cases", {
                patient_id: patientId,
                diagnosis,
                treatment_notes: treatmentNotes,
                arches: clinical.arch || "both",
                clinical: cleanClinical(), prescription: rx, pricing,
                practice_location_id: practiceLocationId || null,
                planning_mode: planningMode,
            });
            setDraftCase(r.data);
            setLastSavedAt(new Date());
            // Stamp the draft id into the URL so refresh resumes seamlessly.
            const next = new URLSearchParams(searchParams);
            next.set("draft", r.data.id);
            setSearchParams(next, { replace: true });
            return r.data;
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't save draft");
            return null;
        } finally {
            setBusy(false);
        }
    }, [draftCase, patientId, diagnosis, treatmentNotes, clinical, cleanClinical, rx, pricing,
        practiceLocationId, planningMode, searchParams, setSearchParams]);

    /* ─── Per-step demo-fill callbacks (iter98.99bn) ───
       Each callback paints ONE step with the active scenario's data.
       Step 1 also creates (or reuses) a deterministically-named demo
       patient so the wizard can advance without manual selection.
       Step 3 calls the backend to generate + attach demo STL scans,
       intraoral photos, and X-rays in a single round-trip. */
    const fillStep1Demo = useCallback(async () => {
        const s = currentDemoScenario;
        if (!s) { toast.error("Demo data unavailable — try again in a moment."); return; }
        try {
            // Pick a primary location (already pre-selected in most cases)
            const primary = (locations || []).find((l) => l.is_primary) || (locations || [])[0];
            if (primary && !practiceLocationId) setPracticeLocationId(primary.id);
            setPlanningMode("coe");
            setPricing((p) => ({
                ...p,
                priority_tier_id: null,
                priority_code: "standard",
                priority_label: "Standard TAT",
                priority_fee: 0,
                priority_tat_days: 10,
            }));
            setScanSource("upload");
            // Find or create a "Demo · {Name}" patient
            const demoName = `Demo · ${s.patient.name}`;
            const existing = (patients || []).find((p) => p.name === demoName);
            if (existing) {
                setPatientId(existing.id);
            } else {
                const r = await api.post("/doctor/patients", {
                    name: demoName,
                    phone: s.patient.phone,
                    email: s.patient.email || "",
                    gender: s.patient.gender || "",
                });
                const newPatient = r.data;
                setPatients((prev) => [newPatient, ...(prev || [])]);
                setPatientId(newPatient.id);
            }
            // iter98.99bo — Ensure the doctor has a signature on file. The
            // wizard's Submit button is hard-gated on the doctor having a
            // saved e-sig in their profile (this is a regulatory check).
            // For demos, we drop a tiny SVG-encoded data-URL signature so
            // the demo doctor doesn't have to detour into Profile → Branding
            // mid-demo. Skipped if they already have one.
            try {
                const prof = await api.get("/doctor/profile");
                if (!prof.data?.doctor_signature_data_url) {
                    const sigSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 80"><path d="M10 55 q40 -50 70 0 q15 20 35 -10 q20 -30 55 5 q25 25 55 -15 q15 -10 80 -5" stroke="#1d2530" stroke-width="2.4" fill="none" stroke-linecap="round" stroke-linejoin="round"/><text x="195" y="72" font-family="serif" font-size="11" fill="#1d2530">DEMO signature</text></svg>`;
                    const sigDataUrl = "data:image/svg+xml;base64," + btoa(sigSvg);
                    await api.put("/doctor/profile", { doctor_signature_data_url: sigDataUrl });
                    setScannerProfile((p) => ({ ...(p || {}), doctor_signature_data_url: sigDataUrl }));
                }
            } catch { /* non-fatal — Step 6 demo fill will surface a clearer error */ }
            toast.success(`Demo Step 1 filled — patient "${demoName}"`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't fill Step 1");
        }
    }, [currentDemoScenario, locations, practiceLocationId, patients]);

    const fillStep2Demo = useCallback(() => {
        const s = currentDemoScenario;
        if (!s) { toast.error("Demo data unavailable."); return; }
        setClinical((c) => ({ ...c, ...s.clinical }));
        setDiagnosis(s.diagnosis || "");
        setTreatmentNotes(s.treatment_notes || "");
        toast.success("Demo Step 2 filled — Clinical exam");
    }, [currentDemoScenario]);

    const fillStep3Demo = useCallback(async () => {
        const draft = await ensureDraft();
        if (!draft) return;
        try {
            await api.post(`/demo/cases/${draft.id}/attach-fixtures`, null, {
                params: { scenario_idx: demoScenarioIdx % 3 },
            });
            toast.success("Demo Step 3 filled — 12 fixture files attached");
            // Trigger ScansPhotosStep to refresh by bumping draftCase (it
            // reads files via case detail). Easiest is to re-fetch the
            // draft so the uploads map repopulates.
            try {
                const r = await api.get(`/cases/${draft.id}`);
                setDraftCase(r.data);
                const u = {};
                (r.data.files || []).forEach((f) => {
                    if (!f.is_deleted) u[f.kind] = { uploaded: true, file: { name: f.name, size: f.size, id: f.id } };
                });
                setUploads(u);
            } catch { /* non-fatal */ }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't attach demo files");
        }
    }, [ensureDraft, demoScenarioIdx]);

    const fillStep4Demo = useCallback(() => {
        const s = currentDemoScenario;
        if (!s) { toast.error("Demo data unavailable."); return; }
        // Step 4 (Treatment Plan) covers objectives + per-tooth.
        setRx((prev) => ({
            ...prev,
            objectives: s.rx.objectives || [],
            // Per-tooth structures are scenario-agnostic stubs — provides
            // visual content but doesn't lock to the clinical class.
            attachments_plan: prev.attachments_plan.length ? prev.attachments_plan : [
                { tooth: "13", size: "medium", shape: "optimized", purpose: "rotation", stage_onset: 1, notes: "Canine rotation control" },
                { tooth: "23", size: "medium", shape: "optimized", purpose: "rotation", stage_onset: 1 },
                { tooth: "14", size: "medium", shape: "rect_horizontal", purpose: "anchor", stage_onset: 2 },
                { tooth: "24", size: "medium", shape: "rect_horizontal", purpose: "anchor", stage_onset: 2 },
            ],
            precision_cuts: prev.precision_cuts.length ? prev.precision_cuts : [
                { tooth: "13", position: "buccal", purpose: "elastic_hook", depth_mm: 0.6, stage_onset: 5 },
                { tooth: "23", position: "buccal", purpose: "elastic_hook", depth_mm: 0.6, stage_onset: 5 },
            ],
            bite_ramps: prev.bite_ramps?.enabled ? prev.bite_ramps : {
                enabled: true, position: "upper_anterior",
                teeth: ["13", "12", "11", "21", "22", "23"],
                notes: "Lingual ramps to open the bite, stages 2-10.",
            },
            tooth_movements_plan: prev.tooth_movements_plan.length ? prev.tooth_movements_plan : [
                { tooth: "11", directions: ["labial"], movement_type: "tip", magnitude: 1.0, start_stage: 1, end_stage: 12, anchorage: "moderate" },
                { tooth: "21", directions: ["labial"], movement_type: "tip", magnitude: 1.0, start_stage: 1, end_stage: 12, anchorage: "moderate" },
                { tooth: "13", directions: ["distal"], movement_type: "translation", magnitude: 1.5, amount_mm: 1.5, start_stage: 1, end_stage: 14, anchorage: "high" },
                { tooth: "23", directions: ["distal"], movement_type: "translation", magnitude: 1.5, amount_mm: 1.5, start_stage: 1, end_stage: 14, anchorage: "high" },
            ],
        }));
        toast.success("Demo Step 4 filled — Treatment plan + objectives");
    }, [currentDemoScenario]);

    const fillStep5Demo = useCallback(() => {
        const s = currentDemoScenario;
        if (!s) { toast.error("Demo data unavailable."); return; }
        setRx((prev) => ({ ...prev, ...s.rx }));
        // iter98.99bo — Unconditionally populate planTimeline so that if
        // the doctor later switches from CoE to Self-Plan the aligner
        // counts are already in place. In CoE mode the timeline is
        // ignored, so this is a no-op there.
        setPlanTimeline((prev) => ({ ...prev, ...s.plan_timeline }));
        toast.success("Demo Step 5 filled — Lab instructions");
    }, [currentDemoScenario]);

    const fillStep6Demo = useCallback(async () => {
        // iter98.99bp — Step 6 demo fill: knocks down the consent gate so
        // the demo doctor can actually click "Submit to Lab". Feasibility
        // ack was removed from the Submit gate in this iter, so this
        // method only ticks the 4 consent boxes inside
        // PrescriptionComplianceBlock. (Signature itself is auto-applied
        // via the Submit click — no need to call signNow() here.)
        try {
            if (prescriptionSignApiRef?.current?.tickAllConsents) {
                prescriptionSignApiRef.current.tickAllConsents();
            }
            toast.success(planningMode === "doctor"
                ? "Demo Step 6 filled — consents ticked. Click Submit to Lab."
                : "Demo Step 6 filled — consents ticked. Click Submit to Planning.");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't fill Step 6");
        }
    }, [planningMode]);

    const fillCurrentStepDemo = useCallback(() => {
        if (step === 1) return fillStep1Demo();
        if (step === 2) return fillStep2Demo();
        if (step === 3) return fillStep3Demo();
        if (step === 4) return fillStep4Demo();
        if (step === 5) return fillStep5Demo();
        if (step === 6) return fillStep6Demo();
        return Promise.resolve();
    }, [step, fillStep1Demo, fillStep2Demo, fillStep3Demo, fillStep4Demo, fillStep5Demo, fillStep6Demo]);

    /* Sync clinical/prescription/pricing edits onto the persisted draft
       so the Review step shows the latest values without the user having
       to re-save. For child cases (pre-spawned drafts) this is the ONLY
       place where the tier + priority picks reach the backend — the new
       case POST is never called for them.
    /* iter97.90 — also pushes patient_id, planning_mode, and
       practice_location_id (these used to be set only at POST /cases
       time; now stub cases get them via PUT once the doctor picks).
       iter98.33 — autosave used to silently swallow ALL errors here,
       which hid a critical 403 ("Patient is not linked to this doctor")
       and let doctors submit cases with `patient_id: null`. We now
       surface server-side validation errors (4xx) as toasts so the
       doctor can act, while still keeping network blips non-fatal. */
    const syncDraft = useCallback(async () => {
        if (!draftCase) return;
        try {
            const r = await api.put(`/cases/${draftCase.id}`, {
                patient_id: patientId || undefined,
                planning_mode: planningMode || undefined,
                practice_location_id: practiceLocationId || undefined,
                diagnosis, treatment_notes: treatmentNotes,
                arches: clinical.arch || "both",
                clinical: cleanClinical(), prescription: rx, pricing,
                // iter98.99e1 — persist the CoE-Support compliance pick so
                // it round-trips on resume. Sent only when explicitly picked
                // (boolean); null is omitted so the server's `exclude_none`
                // body parser leaves the field untouched when syncDraft
                // fires before the doctor reaches the Step 1 question.
                coe_support_compliance: (
                    typeof coeSupportCompliance === "boolean"
                        ? coeSupportCompliance
                        : undefined
                ),
            });
            setDraftCase(r.data);
            setLastSavedAt(new Date());
            // iter97.39 — Self-plan doctors also persist Phase B timeline
            // fields (aligner counts, wear cadence, start date, milestones,
            // refinement-expected, dual recall, phase breaks) to case.plan
            // via the doctor-plan endpoint. CoE doctors don't author the
            // plan themselves so we skip this call for them.
            if (planningMode === "doctor") {
                const planPayload = {};
                // Coerce numeric inputs (Input type='number' returns strings).
                if (planTimeline.aligner_count_upper !== "") planPayload.aligner_count_upper = Number(planTimeline.aligner_count_upper);
                if (planTimeline.aligner_count_lower !== "") planPayload.aligner_count_lower = Number(planTimeline.aligner_count_lower);
                if (planTimeline.wear_schedule_default_days !== "") planPayload.wear_schedule_default_days = Number(planTimeline.wear_schedule_default_days);
                if (planTimeline.start_date) planPayload.start_date = planTimeline.start_date;
                if ((planTimeline.milestones || []).length) planPayload.milestones = planTimeline.milestones;
                if (planTimeline.refinement_expected) planPayload.refinement_expected = true;
                if (planTimeline.refinement_expected_reason) planPayload.refinement_expected_reason = planTimeline.refinement_expected_reason;
                if (planTimeline.retention_recall_first_months !== "") planPayload.retention_recall_first_months = Number(planTimeline.retention_recall_first_months);
                if (planTimeline.retention_recall_second_months !== "") planPayload.retention_recall_second_months = Number(planTimeline.retention_recall_second_months);
                if ((planTimeline.phase_breaks || []).length) planPayload.phase_breaks = planTimeline.phase_breaks;
                // iter98.99p — 3rd-Party Deliverable round-trip.
                if (planTimeline.external_link) planPayload.external_link = planTimeline.external_link;
                if (planTimeline.video_file_id) planPayload.video_file_id = planTimeline.video_file_id;
                if (planTimeline.planning_software) planPayload.planning_software = planTimeline.planning_software;
                if (planTimeline.planning_software_version) planPayload.planning_software_version = planTimeline.planning_software_version;
                if (planTimeline.planning_software_other) planPayload.planning_software_other = planTimeline.planning_software_other;
                if (Object.keys(planPayload).length) {
                    try { await api.put(`/cases/${draftCase.id}/doctor-plan`, planPayload); }
                    catch { /* non-fatal — surface again at submit-time */ }
                }
            }
        } catch (err) {
            // Server-side validation failures (4xx) are usually actionable
            // by the doctor — surface them. Network blips / 5xx stay silent
            // so the wizard can recover on the next nav step.
            const status = err?.response?.status;
            const detail = err?.response?.data?.detail;
            if (status >= 400 && status < 500 && detail) {
                toast.error(detail);
                // Tag so submitCase's outer catch doesn't double-toast.
                err.__alreadyToasted = true;
                // Re-throw so callers (notably submitCase) abort instead of
                // proceeding with stale draft state. goNext catches and
                // simply doesn't advance the step.
                throw err;
            }
            /* network / 5xx — non-fatal, autosave will retry next nav */
        }
    }, [draftCase, patientId, planningMode, practiceLocationId,
        diagnosis, treatmentNotes, clinical, cleanClinical, rx, pricing, planTimeline,
        coeSupportCompliance]);

    const goNext = async () => {
        // iter97.90 — Stub-case minted on mount means draftCase is set
        // from Step 1. Push edits via syncDraft on EVERY forward
        // transition (patient/planning/clinical/etc.). Legacy ensureDraft
        // fallback only fires if the stub mint failed earlier.
        // iter98.33 — syncDraft now re-throws 4xx errors (e.g. the patient
        // ownership 403). Catch them here so the wizard stays put on the
        // current step with the actionable toast already visible to the
        // doctor, instead of advancing on a stale draft.
        try {
            if (draftCase && step !== lastStep) {
                await syncDraft();
            } else if (step === 2 && !draftCase) {
                await ensureDraft();  // legacy fallback when stub mint failed
            }
        } catch {
            return;  // toast already shown by syncDraft / ensureDraft
        }
        setStep((s) => Math.min(lastStep, s + 1));
    };
    const goBack = () => setStep((s) => Math.max(1, s - 1));

    const uploadSlot = async (slotKey, file) => {
        const draft = await ensureDraft();
        if (!draft) return;
        // iter101.16 — Real-time upload progress. Doctors on Step 3 uploading
        // large STL scans had zero feedback beyond a spinning "Uploading"
        // label; the file would sit uploading for 20-40s and doctors would
        // fear the browser had hung. Progress % + a live bar reassures them
        // the byte stream is flowing.
        setUploads((u) => ({ ...u, [slotKey]: { name: file.name, size: file.size, uploading: true, progress: 0 } }));
        const form = new FormData();
        form.append("file", file);
        try {
            const r = await api.post(`/cases/${draft.id}/upload?kind=${slotKey}`, form, {
                headers: { "Content-Type": "multipart/form-data" },
                onUploadProgress: (evt) => {
                    if (!evt || !evt.total) return;
                    const pct = Math.min(99, Math.round((evt.loaded / evt.total) * 100));
                    setUploads((u) => ({
                        ...u,
                        [slotKey]: { ...(u[slotKey] || {}), name: file.name, size: file.size, uploading: true, progress: pct },
                    }));
                },
            });
            setUploads((u) => ({ ...u, [slotKey]: { name: r.data.name, uploaded: true, id: r.data.id, size: r.data.size || file.size, progress: 100 } }));
            toast.success(`${slotKey} uploaded`);
        } catch (err) {
            setUploads((u) => { const c = { ...u }; delete c[slotKey]; return c; });
            toast.error(err.response?.data?.detail || `Upload failed for ${slotKey}`);
        }
    };

    /* Remove a previously uploaded file from a slot. The backend soft-deletes
       so the audit trail stays intact; the UI just clears the slot back to
       an empty pickable state. */
    const removeSlot = async (slotKey) => {
        const state = uploads[slotKey];
        if (!state?.id) return;
        if (!(await confirmDialog({ title: `Remove "${state.name}" from this case?`, tone: "danger" }))) return;
        setUploads((u) => ({ ...u, [slotKey]: { ...u[slotKey], removing: true } }));
        try {
            await api.delete(`/cases/files/${state.id}`);
            setUploads((u) => { const c = { ...u }; delete c[slotKey]; return c; });
            toast.success(`${slotKey} removed`);
        } catch (err) {
            setUploads((u) => { const c = { ...u }; if (c[slotKey]) delete c[slotKey].removing; return c; });
            toast.error(err.response?.data?.detail || "Could not remove file");
        }
    };

    const submitCase = async () => {
        if (!draftCase) return;
        // iter101.85 — Mark as clean exit so the unmount cleanup skips
        // the "Draft saved" toast (the submit hero dialog + its own
        // toasts cover the confirmation story here).
        cleanExitRef.current = true;
        setBusy(true);
        try {
            /* iter98.99ai — Step 1 of the merged "Sign & Submit" flow.
               Drive the e-signature ingestion BEFORE we POST the case
               submission. The bridge returns false on validation /
               network failure (the block toasts its own error) so we
               abort cleanly without lying to the doctor about state. */
            const signApi = prescriptionSignApiRef.current;
            if (signApi && !signApi.isAlreadySigned) {
                const signedOk = await signApi.signNow?.();
                if (!signedOk) {
                    setBusy(false);
                    return;
                }
            }
            await syncDraft();
            // Self-plan doctors who re-opened the wizard from the case
            // detail's "Edit Plan" button arrive with `status === "plan_authoring"`.
            // For them, "Submit" means "send the now-edited plan to the lab"
            // (PUT /submit-to-production), NOT a fresh draft submit
            // (POST /submit — which rejects non-draft cases with 400).
            if (draftCase.status === "plan_authoring") {
                await api.put(`/cases/${draftCase.id}/submit-to-production`);
                // iter98.69 — Show the premium confirmation dialog
                // instead of a transient sonner toast. The dialog
                // takes over the navigation lifecycle — Back to
                // dashboard / View case CTAs each route accordingly.
                // iter98.81 — Use the self-plan-flavoured production
                // flow so doctors who authored the plan themselves see
                // the trust badge + "Lab kicks off your plan as-is" copy.
                setSubmittedDialog({
                    open: true,
                    flow: "doctor_self_plan_to_production",
                    code: draftCase.code,
                    caseId: draftCase.id,
                });
            } else {
                await api.post(`/cases/${draftCase.id}/submit`);
                /* iter98.99de — If the doctor committed to a Pay
                   advance / Pay in full payment on the Review step, mint
                   the advance intent immediately after the case is
                   submitted. The case is now locked (no longer draft) so
                   POST /advance-intent is the right surface; the
                   wizard's WizardPaymentCommitment widget already
                   wrote the chosen amount/mode into pricing. We swallow
                   intent failures with a soft warning so the submit
                   itself stays successful — the doctor can re-mint
                   from the case detail's Approve panel if needed. */
                if (planningMode === "doctor") {
                    const mode = pricing?.payment_mode || "later";
                    const amount = Number(pricing?.advance_amount || 0);
                    const pct = Number(pricing?.advance_pct || 0);
                    if ((mode === "advance" || mode === "full") && amount > 0) {
                        try {
                            await api.post(`/cases/${draftCase.id}/advance-intent`, {
                                amount,
                                pct,
                            });
                        } catch (e) {
                            console.warn("advance-intent mint failed", e?.response?.data || e);
                            toast.warning("Submitted! Payment link couldn't be minted — open the case to retry.");
                        }
                    }
                }
                /* iter98.80 — fix wrong dialog copy on doctor self-plan
                 * submit. When `planningMode === "doctor"` the doctor is
                 * authoring their OWN plan; the case status becomes
                 * `submitted_to_production` and goes straight to the lab
                 * — NOT to our CoE planners. Use the self-plan-specific
                 * flow (iter98.81) so we don't tell a self-planning
                 * doctor that "our planners will draft the plan". */
                const isSelfPlan = planningMode === "doctor";
                setSubmittedDialog({
                    open: true,
                    flow: isSelfPlan ? "doctor_self_plan_to_production" : "planning",
                    code: draftCase.code,
                    caseId: draftCase.id,
                });
            }
        } catch (err) {
            // iter98.33 — syncDraft already toasts 4xx errors with the
            // server detail. Skip the duplicate toast in that case
            // (err.__alreadyToasted is set inside syncDraft's catch).
            // Errors from the actual submit POST (e.g. the patient-missing
            // 400 guard) still surface here.
            if (!err?.__alreadyToasted) {
                toast.error(err?.response?.data?.detail || "Submit failed");
            }
            // iter101.85 — Submit failed → doctor stays in the wizard.
            // Reset cleanExitRef so a later sidebar-nav toast still
            // fires normally.
            cleanExitRef.current = false;
            setBusy(false);
        }
    };

    /* iter91 — Discard the draft. Soft-cancels via the existing
       endpoint (keeps an audit trail of intent) and navigates back to
       the Cases page. Only invocable when a draft has been persisted
       (i.e. doctor advanced past Step 2). */
    const discardDraft = async () => {
        if (!draftCase) return;
        const label = draftCase.code || "this draft";
        const ok = await confirmDialog({
            title: "Discard this draft?",
            message: `Discard the draft case ${label}? This can't be undone — but the patient is preserved and you can start a new case for them any time.`,
            confirmText: "Discard",
            tone: "danger",
        });
        if (!ok) return;
        setBusy(true);
        try {
            await api.put(`/cases/${draftCase.id}/cancel`, {
                reason: "Discarded by doctor before submission",
            });
            // iter101.85 — clean exit; skip the silent-save unmount toast.
            cleanExitRef.current = true;
            toast.success("Draft discarded");
            navigate("/doctor/cases");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't discard the draft");
            setBusy(false);
        }
    };

    /* Cancel just navigates away — drafts persist server-side under `status=draft`
       and show up on the My Cases page, so nothing is "lost". iter101.74 —
       Uses the new LeaveWizardDialog (premium hero + itinerary) instead of
       the plain confirmDialog so the confirmation UX matches the rest of
       the case flow. */
    const handleCancel = () => {
        if (draftCase) {
            setLeaveWizard({ open: true });
            return;
        }
        navigate(-1);
    };

    const headerCaseCode = draftCase?.code;
    const caseKind = draftCase?.case_kind || "primary";
    const isChildCase = !!draftCase?.parent_case_id;
    /* iter97.89 — Scan-visit resume detection drives a wholly different
       header treatment. The case already has a patient (locked at scan
       visit booking), scan source is implicitly Invisique IO, and the
       technician has uploaded records. The H1 of the unified
       WizardCaseHeader becomes the patient name automatically; we also
       feed the scan-visit lineage (file count + tech + completed date)
       into a dedicated "Scan source" field in the meta strip. */
    const isScanVisitResume = draftCase?.status === "scan_visit_completed";
    const scanResumeFileCount = draftCase?.scan_visit_file_count || 0;
    // iter98.68 — Anonymize technician identity. The wizard header used
    // to render the individual technician's name; per user request the
    // doctor now sees a generic "Center of Excellence Team" label.
    // Server still keeps the real `scan_visit_technician_name` for
    // audit + admin tools.
    const scanResumeTechName = "Center of Excellence Team";
    const scanResumeCompletedAt = draftCase?.scan_visit_completed_at || "";
    /* iter97.89 — Title fallback when no patient is selected yet (brand
       new case at Step 1). For child cases we still want the special
       "Refinement case" / "Retainer order" labels even after the patient
       loads, but the WizardCaseHeader handles that by preferring
       patient.name over the fallback. So fallback only shows when
       patient is null — which for child cases is essentially never
       (lockedPatient is set during hydration). */
    const titleFallback = caseKind === "refinement"
        ? copy("wizard.refinement_headline", "Refinement case")
        : caseKind === "retainer"
            ? copy("wizard.retainer_headline", "Retainer order")
            : copy("wizard.headline", "New aligner case");
    /* iter97.89 — Patient payload for the unified header. Prefer the full
       record from the doctor's patients list (carries photo_data_url +
       age + gender + human_id); fall back to lockedPatient when resuming
       a scan-visit or child case (limited fields — name + phone + id). */
    const headerPatient = selectedPatient || lockedPatient || null;
    /* iter97.89 — Clinic name for the header right-side strip. Resolves
       from the currently selected practice location; falls back to the
       primary location when nothing is explicitly picked. */
    const headerClinicName = (() => {
        if (!locations?.length) return null;
        const loc = locations.find((l) => l.id === practiceLocationId)
            || locations.find((l) => l.is_primary)
            || locations[0];
        if (!loc) return null;
        return loc.city ? `${loc.name}, ${loc.city}` : loc.name;
    })();
    /* iter101.83 — "Change patient" affordance removed from the
       header per user direction. `canChangePatient` +
       `headerChangePatient` dead-code deleted. If the doctor picks
       the wrong patient they can restart the wizard from the cases
       list; the flow is a fresh draft so no data is lost. */

    return (
        <div className="space-y-6" data-testid="case-submission-page">
            {/* iter101.62 — Header no longer wrapped in a full-bleed
                `bg-background border-b` banner strip. That extra strip
                painted a visible "background" band above the wizard
                that broke visual parity with Patient Profile + Case
                Detail (both of which render their command headers
                directly on the page canvas). Now the wizard header
                sits inline with the rest of the wizard content — same
                grammar the doctor sees everywhere else. */}
            <div className={`${wizardMaxW} mx-auto w-full`}>
                <Link
                    to="/doctor/cases"
                    className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground"
                    data-testid="case-wizard-back-to-cases"
                >
                    <CaretLeft size={12} weight="bold" /> Back to My Cases
                </Link>
                <div className="mt-2.5">
                    <WizardCaseHeader
                            patient={headerPatient}
                            titleFallback={titleFallback}
                            clinicName={headerClinicName}
                            caseCode={headerCaseCode}
                            caseStatus={draftCase?.status || "draft"}
                            planningMode={planningMode}
                            createdAt={draftCase?.created_at}
                            /* iter101.17 — Full Step-1 selection strip.
                               Every step of the wizard now echoes the
                               commitments the doctor made in Step 1 so
                               they never lose sight of what they've
                               picked.
                               iter101.79 — Steps 2–6 collapse the strip
                               into a single "X of 4 picked" chip so
                               downstream content gets more height. */
                            /* iter101.82 — Never compact. User asked
                               to show the full Step-1 selection strip
                               across every step so the doctor keeps
                               all five commitments in peripheral view
                               throughout the wizard. They can Back
                               to Step 1 if they want to edit. */
                            compact={false}
                            scanSource={scanSource}
                            coeSupportCompliance={coeSupportCompliance}
                            /* iter101.82 — Title-case admin-supplied
                               labels ("RUSH" → "Rush") so the header
                               chip stops shouting all-caps at the
                               doctor. Same fix already applied to the
                               Step 1 recap chip in iter101.74. */
                            priorityLabel={(pricing?.priority_label || "")
                                .toString()
                                .toLowerCase()
                                .replace(/\b\w/g, (ch) => ch.toUpperCase())}
                            priorityCode={pricing?.priority_code || ""}
                            priorityTatDays={Number(pricing?.priority_tat_days || 0) || null}
                            isScanVisitResume={isScanVisitResume}
                            scanResumeFileCount={scanResumeFileCount}
                            scanResumeTechName={scanResumeTechName}
                            scanResumeCompletedAt={scanResumeCompletedAt}
                            /* iter101.81 — Compact "Case setup · X of 4"
                               chip on Steps 2–6 becomes a shortcut back
                               to Step 1 so the doctor can review/change
                               their picks without hunting for the dot
                               rail. Only wired when step > 1 (Step 1
                               has no compact chip anyway). */
                            onEditCaseSetup={step > 1 ? () => setStep(1) : null}
                            busy={busy}
                        >
                            {/* iter98.99l — Step indicator now uses
                                per-step accent palette (primary →
                                violet → amber → emerald → rose →
                                primary) so each step has its own
                                color identity. Needs `planningMode`
                                to pick the right palette entry for
                                Step 6 (Choose Tier vs Review). */}
                            <StepIndicator step={step} steps={steps} planningMode={planningMode} />
                            {/* iter100.278/285 — DraftSavedChip moved down
                                into the sticky wizard footer next to
                                "Step X of Y" (see below) per user
                                direction. Not rendered here anymore. */}
                        </WizardCaseHeader>
                    </div>
                    {isChildCase && draftCase?.parent_case_id && (
                        <div className="mt-3">
                            <ChildCaseBanner
                                caseKind={caseKind}
                                parentCaseId={draftCase.parent_case_id}
                                parentCaseCode={draftCase.parent_case_code}
                            />
                        </div>
                    )}
                </div>

            {/* Body — wizard content + step bodies. Bottom padding leaves
                room for the sticky action bar so content never hides under it. */}
            <div className="pb-32">
                <div className={`${wizardMaxW} mx-auto`} data-testid={`case-wizard-step-${step}`}>
                    {hydrating && !metadata && (
                        <div className="text-center text-muted-foreground py-16 text-sm">
                            Loading…
                        </div>
                    )}
                    {/* iter101.74 — WizardStepHero removed per user
                        direction. The step indicator at the top of the
                        wizard already communicates which step we're on;
                        the extra strip added noise without adding
                        signal. `WizardStepHero` is kept in the file in
                        case we want to bring it back on a targeted
                        step later.

                        Kept: the per-step DemoFillButton (sales-rep
                        aid) — still floats top-right on the clinical
                        steps that support it. */}
                    {metadata && [2, 3, 4, 5].includes(step) && (
                        <div className="mb-5 flex justify-end" data-testid={`case-wizard-step${step}-demo-slot`}>
                            <DemoFillButton
                                stepLabel={
                                    step === 2 ? "Clinical Exam"
                                        : step === 3 ? "Scans & Photos"
                                            : step === 4 ? "Treatment Plan"
                                                : "Lab Instructions"
                                }
                                scenarioLabel={currentDemoScenario?.label || ""}
                                scenarioIdx={demoScenarioIdx}
                                onFill={fillCurrentStepDemo}
                                disabled={!currentDemoScenario}
                                testId={`demo-fill-step-${step}`}
                            />
                        </div>
                    )}
                    {/* iter97.89 — The standalone "Resuming from scan visit"
                        banner is gone; that lineage is now carried in the
                        page header as a single subtle chip. Step 1 below
                        also adapts when resuming: the Patient picker and
                        the Scan Source picker collapse (both are pre-set
                        and immutable), leaving only the Submission Mode
                        choice the doctor still has to make. */}
                    {step === 1 && metadata && (
                        <StepCaseSetup
                            patients={filteredPatients}
                            selectedPatient={selectedPatient}
                            query={patientQuery} setQuery={setPatientQuery}
                            patientId={patientId} setPatientId={setPatientId}
                            locations={locations}
                            practiceLocationId={practiceLocationId}
                            setPracticeLocationId={setPracticeLocationId}
                            planningMode={planningMode} setPlanningMode={setPlanningMode}
                            coeSupportCompliance={coeSupportCompliance}
                            setCoeSupportCompliance={setCoeSupportCompliance}
                            lockedPatient={lockedPatient}
                            tiers={tiers} pricing={pricing} setPricing={setPricing}
                            promotions={promotions}
                            priorityTiers={priorityTiers}
                            allowance={allowance}
                            isChildCase={isChildCase}
                            caseKind={caseKind}
                            isScanVisitResume={isScanVisitResume}
                            planningCards={planningCards}
                            wizardCopy={wizardCopy}
                            tierInsights={tierInsights}
                            ioScannerFee={ioScannerFee}
                            complianceFee={complianceFee}
                            scanSourceCards={scanSourceCards}
                            complianceCard={complianceCard}
                            scanServiceHero={scanServiceHero}
                            scanSource={scanSource}
                            setScanSource={setScanSource}
                            scannerProfile={scannerProfile}
                            onOpenScanVisitModal={() => {
                                // iter97.84 — Belt-and-braces guard. The
                                // ScanSourcePicker already disables the
                                // CTA when no patient is selected, but
                                // someone could theoretically trigger
                                // this via dev tools or future entry
                                // points. Silently refuse — the disabled
                                // CTA is the visible cue.
                                if (!patientId) return;
                                setScanVisitModalOpen(true);
                            }}
                            onOpenAddPatient={(prefillName = "") => {
                                setAddPatientPrefillName(typeof prefillName === "string" ? prefillName : "");
                                setAddPatientOpen(true);
                            }}
                            draftCaseId={draftCase?.id || draftCaseIdParam || ""}
                            ensureDraft={ensureDraft}
                        />
                    )}
                    {step === 2 && metadata && (
                        <>
                            {/* iter100.274 — Step1RecapBanner removed per
                                user direction. Doctors did not want a
                                recap card at the top of Step 2; the step
                                indicator + Back button already convey
                                "you're on Clinical Exam" and Step 1
                                choices are re-editable via Back. */}
                            <StepClinical
                                clinical={clinical} setClinical={setClinical} metadata={metadata}
                                diagnosis={diagnosis} setDiagnosis={setDiagnosis}
                                treatmentNotes={treatmentNotes} setTreatmentNotes={setTreatmentNotes}
                                planningMode={planningMode}
                            />
                        </>
                    )}
                    {step === 3 && metadata && (
                        <ScansPhotosStep
                            slots={metadata.file_slots}
                            uploads={uploads}
                            onUpload={uploadSlot}
                            onRemove={removeSlot}
                            clinical={clinical}
                            setClinical={setClinical}
                            planningMode={planningMode}
                            planTimeline={planTimeline}
                            setPlanTimeline={setPlanTimeline}
                            caseId={draftCase?.id || draftCaseIdParam || ""}
                        />
                    )}
                    {step === 4 && metadata && (
                        <StepTreatmentPlan
                            rx={rx} setRx={setRx} metadata={metadata}
                            planningMode={planningMode}
                        />
                    )}
                    {/* iter97.60 — Step 5 (Attachments) removed. Per-tooth
                        attachments, precision cuts, and bite ramps now live
                        inside Step 4 (Treatment Plan) as a tabbed accordion,
                        mirroring the planner-side UI. Subsequent steps
                        shifted down by one. */}
                    {step === 5 && metadata && (
                        <StepLabInstructions
                            rx={rx} setRx={setRx} metadata={metadata}
                            planningMode={planningMode}
                            planTimeline={planTimeline}
                            setPlanTimeline={setPlanTimeline}
                            clinical={clinical} setClinical={setClinical}
                            selectedTier={tiers.find((t) => t.id === pricing.tier_id) || null}
                            tiers={tiers}
                        />
                    )}
                    {/* iter98.99o — Self-Plan no longer routes through
                        the old "Choose Tier" step. Both modes now render
                        Review at Step 6:
                          · CoE: planner has set the tier upstream
                          · Self-Plan: tier auto-assigned from aligner
                            count entered in Lab Instructions
                        iter101.62 — Dead `StepChooseTier` was deleted. */}
                    {step === 6 && (
                        <StepReview
                            patient={selectedPatient} diagnosis={diagnosis} notes={treatmentNotes}
                            clinical={clinical} rx={rx} setRx={setRx} uploads={uploads} draftCase={draftCase}
                            pricing={pricing} setPricing={setPricing} planningMode={planningMode}
                            draftCaseId={draftCase?.id || draftCaseIdParam || ""}
                            ensureDraft={ensureDraft}
                            /* iter98.99o — Pass auto-tier-assignment
                               metadata so the Review screen can show
                               the "Tier & Pricing (Auto-Assigned)"
                               card at the top for Self-Plan cases. */
                            autoTier={autoSelfPlanTier}
                            planTimeline={planTimeline}
                            /* iter98.99u — Pass priorityTiers + IO scanner
                               fee so the Self-Plan doctor can pick speed
                               and request the IO scanning service from
                               the Review screen. */
                            priorityTiers={priorityTiers}
                            ioScannerFee={ioScannerFee}
                            promotions={promotions}
                            /* iter101.17 — Pass the full Step 1 selection
                               tuple so the CoE Order Preview card can
                               surface every commitment (scan source,
                               planning mode, compliance monitoring) and
                               not just the two pricing line items. */
                            scanSource={scanSource}
                            coeSupportCompliance={coeSupportCompliance}
                            complianceFee={complianceFee}
                            /* iter98.99an — Prescription compliance gate
                               (Indian medical regulations). Threaded so
                               the new e-signature + checklist block in
                               StepReview can talk to the backend and so
                               the wizard's Submit button can gate on
                               whether the doctor has signed yet. */
                            doctorProfile={scannerProfile}
                            prescriptionSigned={prescriptionSigned}
                            setPrescriptionSigned={setPrescriptionSigned}
                            initialCompliance={draftCase?.prescription_compliance || null}
                            onDoctorProfileUpdate={(patch) => setScannerProfile((p) => ({ ...(p || {}), ...patch }))}
                            /* iter98.99ai — Imperative ref so submitCase()
                               can drive POST /prescription/sign in the
                               same click as POST /submit. See submitCase. */
                            signApiRef={prescriptionSignApiRef}
                        />
                    )}
                </div>
            </div>

            {/* Sticky bottom action bar — iter101.77 redesign.
                Now carries three visual upgrades:
                  · Slim primary-tinted progress bar pinned to the top
                    edge so the doctor gets an ambient sense of how
                    far along they are — no need to scan copy.
                  · Segmented "Step X of Y" dot rail replaces the plain
                    text. Filled dots (primary) for completed steps,
                    hollow dots (border) for remaining. Each dot slides
                    in when a new step activates so progress feels
                    earned.
                  · Softer top shadow + gradient background so the bar
                    reads as a floating console instead of a bare
                    border-t line. */}
            <div
                className={`sticky bottom-0 z-30 mt-6 ${wizardMaxW} mx-auto w-full`}
                data-testid="case-wizard-sticky-bar"
            >
                {/* iter101.87 — Top-edge teal progress fill removed per
                    user direction. The dot rail below already
                    communicates step progress; the extra fill added
                    visual noise without complementary information. */}
                <div className="bg-gradient-to-t from-background via-background/98 to-background/85 backdrop-blur-xl border-t border-border/80 shadow-[0_-8px_32px_-12px_rgba(15,23,42,0.12)] rounded-md">
                    {/* iter101.80 — Bar body aligned to the wizard's
                        `max-w-5xl mx-auto` so the stepper starts at
                        the case-content left edge and the buttons
                        finish at its right edge. Draft chip sits
                        directly to the left of Cancel. */}
                    <div className="px-4 sm:px-6 py-2.5 flex items-center justify-between gap-3">
                        {/* LEFT — Segmented step rail. Aligned to
                            the wizard content's left edge. */}
                        <div className="hidden md:flex items-center gap-3 min-w-0">
                            <div className="inline-flex items-center gap-2">
                                <div
                                    className="flex items-center gap-1"
                                    role="progressbar"
                                    aria-valuemin={1}
                                    aria-valuemax={lastStep}
                                    aria-valuenow={step}
                                    data-testid="case-wizard-step-rail"
                                >
                                    {Array.from({ length: lastStep }, (_, i) => {
                                        const n = i + 1;
                                        const state = n < step ? "done" : n === step ? "active" : "todo";
                                        return (
                                            <span
                                                key={n}
                                                aria-hidden
                                                className={`block rounded-full transition-all duration-300 ease-out ${
                                                    state === "active"
                                                        ? "h-1.5 w-6 bg-primary shadow-[0_0_6px_rgba(20,184,166,0.5)]"
                                                        : state === "done"
                                                            ? "h-1.5 w-4 bg-primary/60"
                                                            : "h-1.5 w-4 bg-border"
                                                }`}
                                            />
                                        );
                                    })}
                                </div>
                                <span
                                    className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-muted-foreground whitespace-nowrap"
                                    data-testid="case-wizard-step-of"
                                >
                                    Step{" "}
                                    <span className="text-primary tabular-nums text-[11.5px] font-display font-bold">
                                        {step}
                                    </span>
                                    <span className="mx-0.5 opacity-60">/</span>
                                    <span className="tabular-nums text-[11.5px] font-display font-bold text-foreground/80">
                                        {lastStep}
                                    </span>
                                </span>
                            </div>
                        </div>

                        {/* RIGHT — Draft chip · Cancel · Back · Next.
                            iter101.80 — Draft chip nests inside the
                            right action cluster (immediately left of
                            Cancel) so the whole "what you can do here"
                            group lives together and hugs the wizard's
                            right edge. */}
                        <div className="flex items-center gap-2">
                            {draftCase && (
                                <DraftSavedChip lastSavedAt={lastSavedAt} />
                            )}
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleCancel}
                                disabled={busy}
                                data-testid="case-wizard-cancel"
                                className="text-muted-foreground hover:text-foreground"
                            >
                                Cancel
                            </Button>
                            <span className="h-6 w-px bg-border mx-0.5" aria-hidden />
                            {step > 1 && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={goBack}
                                    disabled={busy}
                                    data-testid="case-wizard-back"
                                    className="group"
                                >
                                    <CaretLeft
                                        size={14}
                                        className="mr-1 transition-transform group-hover:-translate-x-0.5"
                                    />{" "}
                                    Back
                                </Button>
                            )}
                            {step < lastStep && (
                                <Button
                                    size="sm"
                                    onClick={goNext}
                                    disabled={!canNext || busy}
                                    data-testid="case-wizard-next"
                                    className="group bg-gradient-to-b from-primary to-primary/95 hover:from-primary/95 hover:to-primary shadow-sm hover:shadow-md transition-all"
                                >
                                    {busy ? "Saving…" : "Next"}
                                    <CaretRight
                                        size={14}
                                        className="ml-1 transition-transform group-hover:translate-x-0.5"
                                    />
                                </Button>
                            )}
                            {step === lastStep && (() => {
                            // iter98.99bp — Feasibility gate removed from
                            // Submit. The visible feasibility-ack ladder
                            // was removed from Step 6 in iter98.99w but
                            // the gate was kept, creating a silent block
                            // with no way for the doctor to clear it.
                            // The signed Consent Policy already captures
                            // the doctor's clinical commitment, so we drop
                            // feasibilityOk from the gate entirely.
                            // iter98.99o — Self-Plan: block submit if the
                            // total aligner count exceeds the maximum tier
                            // cap (typically 40). `autoSelfPlanTier.error`
                            // is set whenever that condition is hit.
                            const tierOverflow = !!autoSelfPlanTier?.error;
                            // iter98.99an — Prescription compliance gate
                            // (e-signature + 4 consent boxes). Applies to
                            // BOTH Self-Plan and CoE flows per user
                            // direction. The flag flips true only after the
                            // backend confirms the doctor has POSTed
                            // /prescription/sign with a valid signature
                            // and all 4 boxes ticked.
                            const submitDisabled = busy || tierOverflow || !prescriptionSigned;
                            const hasProfileSig = !!scannerProfile?.doctor_signature_data_url;
                            const reason = tierOverflow
                                ? autoSelfPlanTier.error
                                : !prescriptionSigned
                                    ? (hasProfileSig
                                        ? "Agree to the Consent Policy above to enable Submit"
                                        : "Add your e-signature to your profile to submit cases")
                                    : undefined;
                            /* iter98.99at — Per user direction, the
                               sub-label "you're signing this Rx" was
                               removed and the button label simplified
                               to "Submit to Lab" / "Send to Lab" /
                               "Submit to Planning" regardless of
                               whether the doctor has signed yet. The
                               signing still happens in the same click
                               (CTA wires through signApiRef →
                               PrescriptionComplianceBlock.signNow before
                               POST /api/cases/{id}/submit); the language
                               is just terser since "Submit" implies the
                               audit commitment in context with the
                               Consent Policy tick above. */
                            const isAlreadySigned = !!draftCase?.prescription_compliance?.signed_at;
                            const baseLabel = busy
                                ? "Submitting…"
                                : draftCase?.status === "plan_authoring"
                                    ? "Send to Lab"
                                    : planningMode === "doctor"
                                        ? "Submit to Lab"
                                        : "Submit to Planning";
                            return (
                                <div className="flex flex-col items-end gap-1">
                                    <Button
                                        size="sm"
                                        onClick={submitCase}
                                        disabled={submitDisabled}
                                        className="bg-primary"
                                        data-testid="case-wizard-submit"
                                        title={reason}
                                    >
                                        <CheckCircle size={14} className="mr-1.5" weight="fill" />
                                        {baseLabel}
                                    </Button>
                                    {/* iter101.74 — Rose visible reason chip
                                        removed per user direction. The disabled
                                        Submit button + inline copy inside the
                                        Consent Policy card already convey why
                                        the doctor can't submit yet; the extra
                                        chip felt like a scolding banner. Kept
                                        the `title` tooltip on the button as a
                                        soft hover-only fallback. */}
                                </div>
                            );
                        })()}
                    </div>
                </div>
            </div>
            </div>

            {/* iter97.83 — Wizard quick-create scan-visit modal. Mounts at
                the page level (not inside a step) so it renders above
                everything when the doctor picks "Book Invisique IO
                Scanning Service" from the Step 1 scan-source picker.
                On success we navigate the doctor to the new case
                detail page (status `scan_visit_scheduled`). */}
            <RequestScanVisitModal
                open={scanVisitModalOpen}
                onClose={() => setScanVisitModalOpen(false)}
                onCreated={(visit) => {
                    // iter98.99g — Replace the legacy "navigate to patient
                    // profile" with a celebratory CaseSubmittedDialog so
                    // the doctor gets a visual confirmation of WHEN the
                    // technician is arriving + what happens next. Dialog
                    // primary CTA ("Done") returns to the doctor dashboard
                    // (the case is already in `scan_visit_scheduled` and
                    // can't be progressed further until scans land), and
                    // the redirect is captured here so closing via overlay
                    // / ESC also lands the doctor in a sane place.
                    const caseId = visit?.case_id;
                    const meta = visit?.__booking_meta || null;
                    setSubmittedDialog({
                        open: true,
                        flow: "scan_visit_booked",
                        code: visit?.case_code || "",
                        caseId: caseId || "",
                        visitMeta: meta,
                        redirect: patientId ? `/doctor/patients/${patientId}` : "/doctor",
                    });
                }}
                patient={
                    lockedPatient ||
                    (patientId
                        ? {
                            id: patientId,
                            name: (patients.find((p) => p.id === patientId)?.name) || "Patient",
                        }
                        : null)
                }
                mode="wizard_quick_create"
                fee={ioScannerFee}
                /* iter98.99g — Phase-2 UX: IO-Service booking is now
                   lightweight (visit-only). The doctor no longer
                   commits to Submission Mode / Compliance / Mfg Speed
                   at booking — those are picked on resume when they
                   have the actual scans + clinical context. Pass null
                   so the stub case is created with no planning_mode
                   snapshot, and the resume wizard prompts fresh. */
                planningMode={null}
            />

            {/* iter97.87 — Inline Add Patient dialog. Opens via the
                "Add a new Patient" link inside Step 1. On success we
                refresh the patient list AND auto-select the new
                patient so the wizard advances seamlessly. */}
            <AddPatientDialog
                open={addPatientOpen}
                onOpenChange={(next) => {
                    setAddPatientOpen(next);
                    if (!next) setAddPatientPrefillName("");
                }}
                defaultName={addPatientPrefillName}
                onCreated={(p) => {
                    // Optimistically append the new patient + select it
                    // so the doctor doesn't have to retype the name.
                    // iter97.88 — `setQuery` was a typo for `setPatientQuery`
                    // (the actual setter in this scope; `setQuery` only
                    // exists as a prop name inside <Step1>). The undefined
                    // call was throwing ReferenceError, which bubbled into
                    // the dialog's catch and triggered a bogus "Failed"
                    // toast even though the patient was created fine.
                    setPatients((prev) => {
                        const next = [p, ...prev.filter((x) => x.id !== p.id)];
                        return next;
                    });
                    setPatientId(p.id);
                    setPatientQuery("");
                }}
            />

            {/* iter98.69 — Premium case-submitted confirmation dialog.
                Owns navigation after a successful submit (View case →
                /doctor/cases/{id}; Back to dashboard → /doctor). The
                dialog also handles overlay/ESC dismiss by routing to
                the case page (never strands the doctor on the wizard). */}
            <CaseSubmittedDialog
                open={submittedDialog.open}
                flow={submittedDialog.flow}
                caseCode={submittedDialog.code}
                visitMeta={submittedDialog.visitMeta}
                onViewCase={() => {
                    setSubmittedDialog((s) => ({ ...s, open: false }));
                    // iter98.99g — scan_visit_booked uses the explicit
                    // `redirect` (patient profile) rather than the case
                    // detail page; everything else navigates to the new
                    // case detail as before.
                    if (submittedDialog.flow === "scan_visit_booked") {
                        navigate(submittedDialog.redirect || "/doctor");
                    } else {
                        navigate(`/doctor/cases/${submittedDialog.caseId}`);
                    }
                }}
                onBackToDashboard={
                    submittedDialog.flow === "scan_visit_booked"
                        ? undefined  // single "Done" CTA only for scan visits
                        : () => {
                            setSubmittedDialog((s) => ({ ...s, open: false }));
                            navigate("/doctor");
                        }
                }
            />

            {/* iter101.74 — Premium "Leave the wizard?" dialog. Same design
                DNA as CaseSubmittedDialog so the doctor sees a familiar
                hero card whether they're bailing on a draft or celebrating
                a submit. */}
            <LeaveWizardDialog
                open={leaveWizard.open}
                caseCode={draftCase?.code || ""}
                onStay={() => setLeaveWizard({ open: false })}
                onSaveDraft={() => {
                    // iter101.84 — Save-as-Draft path. Draft is already
                    // persisted server-side (every field autosaves via
                    // PUT /cases/{id}); we just need to close the dialog
                    // and route home. Same outcome as the old "Leave to
                    // My Cases" button.
                    // iter101.85 — Mark clean exit; this handler already
                    // fires its own toast, so the unmount cleanup should
                    // stay silent.
                    cleanExitRef.current = true;
                    setLeaveWizard({ open: false });
                    toast.success("Draft saved · pick it back up any time");
                    navigate("/doctor");
                }}
                onDiscard={async (reason) => {
                    // iter101.84 — Discard path. Fires PUT /cases/{id}/cancel
                    // (existing backend endpoint) with the doctor's reason,
                    // which flips the case to status="cancelled" and logs
                    // the audit trail. Case disappears from the drafts
                    // list (it's now filtered by cancelled status) so the
                    // pipeline stays clean.
                    if (!draftCase?.id) return;
                    try {
                        await api.put(`/cases/${draftCase.id}/cancel`, { reason });
                        // iter101.85 — Discarded → no "draft saved" toast on unmount.
                        cleanExitRef.current = true;
                        setLeaveWizard({ open: false });
                        toast.success("Case discarded");
                        navigate("/doctor");
                    } catch (err) {
                        toast.error(err.response?.data?.detail || "Couldn't discard case");
                    }
                }}
            />

            {/* iter101.75 — "Welcome back" resume hero. Fires once when the
                wizard hydrates from a real (non-stub) draft so the doctor
                sees exactly what's been captured, and which step they're
                dropping onto. Same design DNA as CaseSubmittedDialog +
                LeaveWizardDialog. */}
            <ResumeDraftDialog
                open={resumeDraft.open}
                caseCode={resumeDraft.snapshot?.code || draftCase?.code || ""}
                resumeStep={resumeDraft.step}
                stepLabel={
                    (stepsFor(resumeDraft.snapshot?.planning_mode)
                        .find((s) => s.id === resumeDraft.step)?.label) || "Case Setup"
                }
                doctorName={scannerProfile?.name || ""}
                progress={buildResumeProgress(resumeDraft.snapshot)}
                onContinue={() => setResumeDraft((r) => ({ ...r, open: false }))}
            />
        </div>
    );
}


/* iter101.75 — buildResumeProgress: derives the "what you've done so far"
 * checklist shown in the ResumeDraftDialog from the raw draft-case snapshot.
 * Order mirrors the wizard's step order so the itinerary reads left-to-right
 * as a natural chronology. `done` is a strict boolean — never null — because
 * the dialog renders the tick / hollow-circle state directly off it. */
function buildResumeProgress(c) {
    if (!c) return [];
    const rows = [];

    // Step 1 · Patient
    rows.push({
        key: "patient",
        label: "Patient picked",
        done: !!c.patient_id,
        detail: c.patient_id ? (c.patient_name || "Patient on file") : "Not yet — pick from your list on Step 1",
    });

    // Step 1 · Planning mode
    rows.push({
        key: "planning",
        label: "Planning mode chosen",
        done: !!c.planning_mode,
        detail: c.planning_mode === "doctor"
            ? "Self-planned — you author the treatment plan"
            : c.planning_mode === "coe"
                ? "Invisique™ Team — our planners draft the plan"
                : "Not yet — pick on Step 1",
    });

    // Step 1 · Manufacturing speed
    const spd = c.pricing?.priority_label || c.pricing?.priority_code || "";
    const prettySpd = spd
        ? spd.toString().toLowerCase().replace(/\b\w/g, (ch) => ch.toUpperCase())
        : "";
    rows.push({
        key: "speed",
        label: "Manufacturing speed",
        done: !!(c.pricing?.priority_tier_id || c.pricing?.priority_code),
        detail: prettySpd ? `${prettySpd} turnaround` : "Not yet — pick on Step 1",
    });

    // Step 2 · Clinical exam / diagnosis
    rows.push({
        key: "clinical",
        label: "Clinical exam captured",
        done: !!c.diagnosis,
        detail: c.diagnosis
            ? "Diagnosis + chief complaint entered"
            : "Not yet — capture on Step 2",
    });

    // Step 3 · Scans + photos
    const liveFiles = (c.files || []).filter((f) => !f.is_deleted);
    rows.push({
        key: "scans",
        label: "Scans + photos uploaded",
        done: liveFiles.length > 0,
        detail: liveFiles.length > 0
            ? `${liveFiles.length} file${liveFiles.length === 1 ? "" : "s"} on file`
            : "Not yet — upload on Step 3",
    });

    // Step 4 · Treatment plan objectives
    const objectives = c.prescription?.objectives || [];
    rows.push({
        key: "objectives",
        label: "Treatment objectives set",
        done: objectives.length > 0,
        detail: objectives.length > 0
            ? `${objectives.length} objective${objectives.length === 1 ? "" : "s"} added`
            : "Not yet — set on Step 4",
    });

    // Step 5 · Lab instructions
    rows.push({
        key: "lab",
        label: "Lab instructions written",
        done: !!c.prescription?.lab_notes,
        detail: c.prescription?.lab_notes
            ? "Notes captured for the lab"
            : "Not yet — write on Step 5",
    });

    // Step 6 · Consent + signature
    const pc = c.prescription_compliance;
    const consentDone = !!(pc?.signed_at);
    rows.push({
        key: "consent",
        label: "Consent + signature",
        done: consentDone,
        detail: consentDone
            ? "Signed and logged — ready to submit"
            : "Final step — sign on Step 6",
    });

    return rows;
}


/* ════════════════════════════════════════════════════════════════════════
   Step-body subcomponents — identical to the legacy wizard. Kept in this
   file because they aren't used elsewhere; one file == one feature.
   ════════════════════════════════════════════════════════════════════════ */

/**
 * Child-case banner — surfaces above the wizard whenever the resumed
 * draft has a `parent_case_id`. Tells the doctor (a) what kind of child
 * case they're filling out, (b) which parent it links to, and (c) gives
 * a one-click "view original submission PDF" link so they can refer
 * back to the case they originally submitted while filling the wizard.
 */
/**
 * DraftSavedChip — iter100.278
 *
 * Small non-intrusive pill that reassures doctors their in-flight
 * wizard work is safe. Text updates via a 30-second `tick` so
 * "just now" gracefully drifts to "1m ago" and beyond without the
 * doctor doing anything.
 */













